import { filter, takeUntil } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import html2pdf from 'html2pdf.js';
import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnChanges, OnDestroy, Output, Renderer2, SimpleChanges, ViewChild, forwardRef, TemplateRef } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { HeaderFooterSettingsComponent } from '../header-footer-settings/header-footer-settings.component';
import { InsertTableDialogComponent } from './insert-table-dialog/insert-table-dialog.component';
import { TablePropertiesDialogComponent, TableProperties } from './table-properties-dialog/table-properties-dialog.component';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FileUploadService } from 'src/app/services/file-upload.service';
import { environment } from 'src/environments/environment';
import { Constant } from 'src/app/constant/constant';
import { FileLikeObject, FileUploader } from 'ng2-file-upload';
import { Subject } from 'rxjs';
import { GeminiService } from 'src/app/services/gemini.service';
import { OpenRouterService } from 'src/app/services/openrouter.service';
import { ChatmodelsService } from 'src/app/services/chatmodels.service';
import { MatSnackBar } from '@angular/material/snack-bar';

type DocRegion = 'header' | 'body' | 'footer';
type ImageAlign = 'left' | 'center' | 'right';

interface ImageCrop {
  enabled: boolean;
  /** fractions 0..1 relative to the displayed image box */
  x: number;
  y: number;
  w: number;
  h: number;
}

interface ImageModel {
  id: string;
  src: string;
  width: number;
  height: number;
  alignment: ImageAlign;
  crop: ImageCrop;
  region: DocRegion;
}


@Component({
  selector: 'calsoft-doc-editor',
  templateUrl: './doc-editor.component.html',
  styleUrls: ['./doc-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DocEditorComponent),
      multi: true
    }
  ]
})
export class DocEditorComponent implements AfterViewInit, OnDestroy, OnChanges, ControlValueAccessor {

  sanitizedUrl: string = '';

  // AI assistant state
  showAiAssistant: boolean = false;
  aiStep: 'input' | 'loading' | 'review' = 'input';
  selectedText: string = "";
  aiCustomPrompt: string = "";
  aiGeneratedResult: string = "";
  isApiKeyConfigured: boolean = false;
  showApiKeyInput: boolean = false;
  tempApiKey: string = "";
  providers: any[] = [];
  selectedProviderId: number | null = null;

  @ViewChild('aiAssistantTemplate') aiAssistantTemplate!: TemplateRef<any>;
  private dialogRef: MatDialogRef<any> | null = null;

  url = '';


  /**
   * Rendering mode:
   * - 'doc': existing full document UI
   * - 'compose': simplified Gmail-like editor surface (no sidenav/top toolbar/warning/status/rulers)
   */
  @Input() mode: 'doc' | 'compose' = 'doc';

  /**
   * Optional external "one-shot" insert channel.
   * If parent sets a new value here, it will be inserted at the current cursor.
   * (Useful when the parent control currently has focus, e.g. mat-select in Email popup.)
   */
  @Input() externalInsertText: string | null = null;

  /**
   * Change this token to force re-processing externalInsertText even if the text is the same.
   * (Example: parent increments a counter each time user chooses Insert Field.)
   */
  @Input() externalInsertToken: number | string | null = null;

  private lastExternalInsertText: string | null = null;
  private lastExternalInsertToken: number | string | null = null;

  /** If set, the editor will load this HTML into the editing surface (preview mode). */
  @Input() previewHtml: string | null = null;

  @Output() doneClicked = new EventEmitter<void>();
  /** Fallback/manual binding: emits HTML on every change (useful if not using reactive forms). */
  @Output() valueChange = new EventEmitter<string>();
  @ViewChild('editor', { static: false }) editor!: ElementRef<HTMLDivElement>;
  @ViewChild('pageWrap', { static: true }) pageWrap!: ElementRef<HTMLDivElement>;
  @ViewChild('page', { static: true }) pageEl!: ElementRef<HTMLDivElement>;
  @ViewChild('topCanvas', { static: true }) topCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('topHandle', { static: true }) topHandle!: ElementRef<HTMLDivElement>;
  @ViewChild('firstLineHandle', { static: true }) firstLineHandle!: ElementRef<HTMLDivElement>;
  @ViewChild('handleTooltip', { static: true }) handleTooltip!: ElementRef<HTMLDivElement>;
  @ViewChild('caretIndicator', { static: true }) caretIndicator!: ElementRef<HTMLDivElement>;

  showWarning = true;
  words = 0;
  chars = 0;
  pageCount = 1;
  currentPage = 1;

  // remember last-applied header/footer so new pages inherit them
  private lastHeaderHtml?: string;
  private lastFooterHtml?: string;

  // responsive
  isSmallScreen = window.innerWidth < 900;
  sidenavOpened = true;

  // editor state
  fonts = ['Roboto', 'Arial', 'Georgia', 'Times New Roman', 'Courier New'];
  sizes = ['8', '9', '10', '11', '12', '14', '16', '18', '20', '22', '24', '26', '28', '36', '48', '72'];
  selectedFont = 'Roboto';
  selectedSize = '11';

  headingStyles: Array<{ label: string; tag: string }> = [
    { label: 'Normal', tag: 'P' },
    { label: 'Heading 1', tag: 'H1' },
    { label: 'Heading 2', tag: 'H2' },
    { label: 'Heading 3', tag: 'H3' },
    { label: 'Heading 4', tag: 'H4' },
    { label: 'Heading 5', tag: 'H5' },
    { label: 'Heading 6', tag: 'H6' }
  ];
  selectedHeadingTag = 'P';
  quickParts = [
    { label: 'Company Name', html: '<strong>{{CompanyName}}</strong>' },
    { label: 'Signature', html: '<div>[Signature]</div>' },
    { label: 'Date', html: '<span>{{Date}}</span>' }
  ];

  // color palette used by the material menu swatches
  presetColors = [
    '#000000', '#444444', '#777777', '#bbbbbb', '#ffffff',
    '#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5',
    '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4caf50',
    '#8bc34a', '#cddc39', '#ffeb3b', '#ffc107', '#ff9800',
    '#ff5722', '#795548', '#9e9e9e'
  ];

  // UI state: which left tab is active (format|insert|fields|image)
  activeTab: 'format' | 'insert' | 'fields' | 'image' = 'format';

  private _onDestroy = new Subject<void>();
  private dpr = window.devicePixelRatio || 1;
  private listeners: Array<() => void> = [];
  private dragging: { type: 'left' | 'top' | null, startX?: number, startY?: number, startValue?: number } = { type: null };
  private footerObserver?: MutationObserver;

  // Track last selection inside an editor/footer so toolbar commands work after clicking buttons.
  private lastSelectionRange: Range | null = null;
  private lastSelectionHost: HTMLElement | null = null;

  // Inline formatting state for toolbar highlighting (Bold/Italic/Underline).
  isBoldActive = false;
  isItalicActive = false;
  isUnderlineActive = false;

  // Current color state for color picker highlighting.
  currentTextColor: string | null = null;
  currentHighlightColor: string | null = null;
  data1: any;

  /**
   * Public API (compose-friendly): insert text at the current caret/selection.
   * Uses the editor's selection tracking so insertion still works after toolbar/select interactions.
   */
  insertTextAtCursor(text: string): void {
    if (text == null) return;
    const editorEl =
      this.getCurrentEditorEl() ||
      this.editor?.nativeElement ||
      (document.querySelector('calsoft-doc-editor .compose-editor') as HTMLElement | null) ||
      null;
    if (!editorEl) return;
    try {
      this.focusAndRestoreSelectionFor(editorEl);
      // Prefer insertText so we don't accidentally inject HTML.
      if (!document.execCommand('insertText', false, String(text))) {
        // Fallback to HTML insertion if insertText is blocked.
        this.insertHtmlIntoEditor(editorEl, this.escapeHtml(String(text)), 'afterHeader');
      } else {
        this.onEditorInput();
        this.updateCaretPosition();
      }
    } catch (e) {
      try {
        this.insertHtmlIntoEditor(editorEl, this.escapeHtml(String(text)), 'afterHeader');
      } catch (e2) { }
    }
  }

  /**
   * Public API: insert HTML at the caret/selection (use carefully).
   */
  insertHtmlAtCursor(html: string): void {
    if (html == null) return;
    const editorEl = this.getCurrentEditorEl() || this.editor?.nativeElement || null;
    if (!editorEl) return;
    try {
      this.focusAndRestoreSelectionFor(editorEl);
      this.insertHtmlIntoEditor(editorEl, String(html), 'afterHeader');
    } catch (e) { }
  }

  // --- Image UX state (Word-like) ---
  selectedImageBlock: HTMLElement | null = null;
  showImageOverlay = false;
  imageOverlay = { left: 0, top: 0, width: 0, height: 0 };
  imageToolbar = { left: 0, top: 0 };
  isCropping = false;
  cropDraft: ImageCrop | null = null;
  private resizeDrag:
    | null
    | {
      handle: string;
      startX: number;
      startY: number;
      startW: number;
      startH: number;
      aspect: number;
    } = null;
  private cropDrag:
    | null
    | {
      mode: 'move' | 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';
      startX: number;
      startY: number;
      start: ImageCrop;
    } = null;

  // image moving (drag/drop reorder)
  private moveDrag:
    | null
    | {
      block: HTMLElement;
      startX: number;
      startY: number;
      active: boolean;
      lastTarget: { container: HTMLElement; before: ChildNode | null } | null;
    } = null;
  private imageDropIndicatorEl: HTMLElement | null = null;

  // --- Table resize state ---
  selectedTable: HTMLTableElement | null = null;
  showTableOverlay = false;
  tableOverlay = { left: 0, top: 0, width: 0, height: 0 };

  // Whole table resize (any handle)
  private tableResizeDrag: {
    table: HTMLTableElement;
    handle: string;
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
  } | null = null;

  // Column resize state
  private columnResizeDrag: {
    table: HTMLTableElement;
    colIndex: number;
    startX: number;
    startWidths: number[];
    cells: HTMLTableCellElement[];
  } | null = null;

  // Row resize state
  private rowResizeDrag: {
    table: HTMLTableElement;
    rowIndex: number;
    startY: number;
    startHeight: number;
    row: HTMLTableRowElement;
  } | null = null;

  // Hover states for cursor change
  tableHoverState: 'none' | 'column' | 'row' | 'corner' = 'none';
  private currentHoverColumn: number = -1;
  private currentHoverRow: number = -1;

  constructor(
    private renderer: Renderer2,
    private zone: NgZone,
    private dialog: MatDialog,
    private fileUploadService: FileUploadService,
    private sanitizer: DomSanitizer,
    private geminiService: GeminiService,
    private openRouterService: OpenRouterService,
    private chatmodelsService: ChatmodelsService,
    private snackBar: MatSnackBar,
    private router: Router) {
    this.url = this.router.url || '';
    this.router.events
      .pipe(filter((e: any) => e instanceof NavigationEnd), takeUntil(this._onDestroy))
      .subscribe(() => { this.url = this.router.url || ''; });
  }

  // Safe accessor for pageWrap native element to avoid runtime errors
  private getPageWrapEl(): HTMLElement | null {
    try {
      return this.pageWrap?.nativeElement || null;
    } catch (e) {
      return null;
    }
  }

  private readonly composeImageUploadLocation = 'calsoftgroup.com';

  /**
   * Sanitize URL for safe usage in templates
   */
  sanitizeUrl(url: string): string {
    try {
      // For URLs that are already trusted or from our own domain, return as-is
      // DomSanitizer is primarily for binding in templates, but for dynamic HTML insertion
      // we need to ensure the URL is safe
      if (!url) return '';

      // If it's a data URL or blob URL, it's safe to use
      if (url.startsWith('data:') || url.startsWith('blob:')) {
        return url;
      }

      // For HTTP/HTTPS URLs, sanitize them
      const sanitized = this.sanitizer.sanitize(1 /* SecurityContext.URL */, url);
      return sanitized || url;
    } catch (e) {
      console.warn('[sanitizeUrl] Failed to sanitize:', url, e);
      return url;
    }
  }

  private dataUrlToBlob(dataUrl: string): { blob: Blob; mime: string } | null {
    try {
      const m = (dataUrl || '').match(/^data:([^;]+);base64,(.+)$/);
      if (!m) return null;
      const mime = m[1];
      const b64 = m[2];
      const binStr = atob(b64);
      const len = binStr.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) bytes[i] = binStr.charCodeAt(i);
      return { blob: new Blob([bytes], { type: mime }), mime };
    } catch (e) {
      return null;
    }
  }

  private uploadComposeImageFileAndReplace(imgEl: HTMLImageElement, file: File): void {
    try {
      const data = new FormData();
      const safeName = (file.name || 'image')
        .replace(/\s/g, '_')
        .replace(/\(/g, '_')
        .replace(/\)/g, '_');
      data.append('files', file, safeName);

      const cid = localStorage.getItem(Constant.enterPriseKey) || '';
      this.fileUploadService.normalUploadFiles(data, 'gmail', this.composeImageUploadLocation).subscribe({
        next: (res: any) => {
          this.data1 = res;
          if (res && res.status_code === '500') {
            this.snackBar.open(res.status_message || 'An error occurred during upload', '×', { panelClass: 'error', verticalPosition: 'bottom', duration: 3000 });
            this.uploadInProgress = false;
            return;
          }
          const fileName = res?.productEntityMediaGallery?.[0]?.fileName;
          if (!fileName) return;
          let imageUrl = environment.commonImageApi + 'gmail/' + fileName;
          if (cid) imageUrl += '&cid=' + cid;
          try {
            imgEl.src = imageUrl;
            imgEl.removeAttribute('data-upload-temp-id');
            imgEl.setAttribute('data-uploaded', '1');

            // Ensure dimensions are properly applied to image and parent block if present
            const applyDimensions = () => {
              const naturalW = imgEl.naturalWidth || 0;
              const naturalH = imgEl.naturalHeight || 0;
              if (naturalW > 0) {
                const maxW = this.getMaxContentWidthFor(imgEl);
                const w = (naturalW < maxW) ? naturalW : maxW;
                const h = Math.round((w * naturalH) / naturalW);
                imgEl.setAttribute('width', String(w));
                imgEl.setAttribute('height', String(h));
                imgEl.style.width = `${w}px`;
                imgEl.style.maxWidth = '100%';
                imgEl.style.height = 'auto';
                const block = imgEl.closest('.image-block') as HTMLElement | null;
                if (block) {
                  this.setImageBlockSize(block, w, h);
                }
              }
            };
            if (imgEl.complete) {
              applyDimensions();
            } else {
              imgEl.onload = () => applyDimensions();
            }
            this.onEditorInput();
          } catch (e) { }
        },
        error: (err) => {
          try {
            imgEl.removeAttribute('data-upload-temp-id');
          } catch (e) { }
          console.warn('Image upload failed; email may not include the image', err);
        }
      });
    } catch (e) {
      // ignore
    }
  }

  private insertComposeImageAndUpload(file: File): void {
    try {
      const tempId = 'imgtmp_' + Math.random().toString(36).slice(2, 9) + '_' + Date.now();
      const id = this.generateImageId();
      const alignment: ImageAlign = 'left';

      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;

        // Pre-calculate natural image dimensions so initial HTML is not collapsed to 100px
        const tempImg = new Image();
        tempImg.onload = () => {
          const naturalW = tempImg.naturalWidth || 600;
          const naturalH = tempImg.naturalHeight || 400;
          const aspect = naturalW / naturalH;
          const maxW = (this.editor?.nativeElement?.clientWidth) || 650;
          const initW = (naturalW > 1 && naturalW < maxW) ? naturalW : (maxW > 300 ? maxW : 650);
          const initH = Math.round(initW / aspect);

          // Use image-block structure with pre-calculated full natural dimensions
          const html =
            `<div class="image-block align-${alignment}" contenteditable="false" ` +
            `data-image-id="${id}" data-image-align="${alignment}" data-image-region="body" ` +
            `data-image-w="${initW}" data-image-h="${initH}" ` +
            `data-crop-enabled="0" data-crop-x="0" data-crop-y="0" data-crop-w="1" data-crop-h="1">` +
            `<div class="image-frame" style="width:${initW}px;height:${initH}px;position:relative;">` +
            `<img class="image-content" data-upload-temp-id="${tempId}" src="${dataUrl}" draggable="false" width="${initW}" height="${initH}" style="object-fit:contain;width:${initW}px;max-width:100%;height:auto;" />` +
            `</div>` +
            `</div><p><br></p>`;

          try {
            this.exec('insertHTML', html);
          } catch (e) {
            try {
              const editorEl = this.getCurrentEditorEl();
              if (editorEl) editorEl.insertAdjacentHTML('beforeend', html);
              this.onEditorInput();
            } catch (e2) { }
          }

          // Upload and replace with real server URL
          setTimeout(() => {
            const block = this.findImageBlockById(id);
            if (!block) return;
            const imgEl = block.querySelector('img[data-upload-temp-id]') as HTMLImageElement | null;
            if (!imgEl) return;

            const data = new FormData();
            const safeName = (file.name || 'image')
              .replace(/\s/g, '_')
              .replace(/\(/g, '_')
              .replace(/\)/g, '_');
            data.append('files', file, safeName);

            const cid = localStorage.getItem(Constant.enterPriseKey) || '';
            this.fileUploadService.normalUploadFiles(data, 'gmail', this.composeImageUploadLocation).subscribe({
              next: (res: any) => {
                this.data1 = res;
                if (res && res.status_code === '500') {
                  this.snackBar.open(res.status_message || 'An error occurred during upload', '×', { panelClass: 'error', verticalPosition: 'bottom', duration: 3000 });
                  this.uploadInProgress = false;
                  return;
                }
                const fileName = res?.productEntityMediaGallery?.[0]?.fileName;
                if (!fileName) return;
                let imageUrl = environment.commonImageApi + 'gmail/' + fileName;
                if (cid) imageUrl += '&cid=' + cid;

                try {
                  imgEl.src = imageUrl;
                  imgEl.removeAttribute('data-upload-temp-id');
                  imgEl.setAttribute('data-uploaded', '1');
                  this.setImageBlockSize(block, initW, initH);
                  this.onEditorInput();
                } catch (e) { }
              },
              error: (err) => {
                try {
                  imgEl.removeAttribute('data-upload-temp-id');
                } catch (e) { }
                console.warn('Image upload failed; email may not include the image', err);
              }
            });
          }, 100);
        };
        tempImg.src = dataUrl;
      };
      reader.readAsDataURL(file);
    } catch (e) {
      // ignore
    }
  }

  private cvaDisabled = false;
  private pendingCvaValue: string | null = null;
  private onChange: (val: string) => void = () => { };
  private onTouched: () => void = () => { };
  private isApplyingCvaValue = false;

  setTab(tab: 'format' | 'insert' | 'fields' | 'image') {
    try { this.activeTab = tab; } catch (e) { }
  }

  /**
   * Format painter stub - copies formatting from current selection and applies to next selection.
   * This is a placeholder: you can implement copy/paste of inline style attributes or use a small state
   * machine to remember the formatting and apply it. For now it logs to console and shows a brief UI cue.
   */
  formatPainter() {
    try {
      console.log('Format painter clicked - implement copy/apply formatting logic');
      // small visual feedback: toggle a short-lived CSS class on the editor
      try {
        const el = this.editor && this.editor.nativeElement;
        if (el) {
          el.classList.add('format-painter-active');
          setTimeout(() => el.classList.remove('format-painter-active'), 600);
        }
      } catch (e) { }
    } catch (e) { console.warn(e); }
  }

  @HostListener('window:resize') onResize() {
    this.isSmallScreen = window.innerWidth < 900;
    this.sidenavOpened = !this.isSmallScreen;
  }

  public uploader: FileUploader = new FileUploader({
    isHTML5: true,
    allowedFileType: ['image'],
    maxFileSize: 16 * 1024 * 1024
  });

  @ViewChild("myInput", { static: false }) myInputVariable!: ElementRef<HTMLInputElement>;

  ngOnInit() {

    this.uploader.onAfterAddingFile = (file) => {
      file.withCredentials = false;
      this.upload();
    };

    // Handle upload complete to ensure queue is cleared
    this.uploader.onCompleteAll = () => {
      console.log('[FileUploader] All uploads complete, clearing queue');
      this.uploader.clearQueue();
    };

    this.sidenavOpened = !this.isSmallScreen;
  }

  ngAfterViewInit() {
    // In compose mode we don't render rulers/handles/canvases, so skip that setup.
    if (this.mode === 'doc') {
      // draw rulers
      this.setupCanvases();
    }

    // selection/caret tracking
    const selHandler = () => {
      this.captureLastSelection();
      this.updateCaretPosition();
      this.updateInlineFormatState();
      this.updateColorState();
    };
    document.addEventListener('selectionchange', selHandler);
    this.listeners.push(() => document.removeEventListener('selectionchange', selHandler));

    // editor events
    const inputEl = this.editor?.nativeElement;
    if (!inputEl) {
      // Shouldn't happen normally (compose template has #editor), but guard to avoid runtime crash.
      return;
    }
    inputEl.addEventListener('keyup', selHandler);
    inputEl.addEventListener('mouseup', selHandler);
    this.listeners.push(() => inputEl.removeEventListener('keyup', selHandler));
    this.listeners.push(() => inputEl.removeEventListener('mouseup', selHandler));

    // CVA: propagate value changes from the editable surface
    const cvaInputHandler = () => {
      try {
        this.onTouched();
        this.cvaEmitValue();
      } catch (e) { }
    };
    inputEl.addEventListener('input', cvaInputHandler);
    this.listeners.push(() => inputEl.removeEventListener('input', cvaInputHandler));

    // Compose mode: when user pastes an image, upload it and replace data: URL with hosted URL
    // so the image appears in the sent email.
    const pasteHandler = (evt: ClipboardEvent) => {
      try {
        if (this.mode !== 'compose') return;
        const cd = evt.clipboardData;
        if (!cd) return;

        // 1) Direct clipboard image file
        const items = Array.from(cd.items || []);
        const imgItem = items.find((it) => (it.type || '').startsWith('image/'));
        if (imgItem) {
          const file = imgItem.getAsFile();
          if (!file) return;
          evt.preventDefault();
          this.insertComposeImageAndUpload(file);
          return;
        }

        // 2) Pasted HTML containing <img src="data:image/...">
        const html = cd.getData('text/html');
        if (html && /<img\b[^>]*src\s*=\s*['"]data:image\//i.test(html)) {
          // Allow paste to happen, then swap any data: images.
          setTimeout(() => {
            try {
              const editorEl = this.getCurrentEditorEl();
              if (!editorEl) return;
              const imgs = Array.from(editorEl.querySelectorAll('img')) as HTMLImageElement[];
              for (const img of imgs) {
                const src = (img.getAttribute('src') || '').trim();
                if (!src || !src.startsWith('data:image/')) continue;
                if (img.getAttribute('data-uploaded') === '1') continue;
                if (img.getAttribute('data-upload-temp-id')) continue;

                const parsed = this.dataUrlToBlob(src);
                if (!parsed) continue;
                const ext = (parsed.mime.split('/')[1] || 'png').toLowerCase();
                const f = new File([parsed.blob], `pasted.${ext}`, { type: parsed.mime });
                img.setAttribute('data-upload-temp-id', 'pasted_' + Date.now());
                this.uploadComposeImageFileAndReplace(img, f);
              }
            } catch (e) { }
          }, 0);
        }
      } catch (e) {
        // ignore
      }
    };
    inputEl.addEventListener('paste', pasteHandler);
    this.listeners.push(() => inputEl.removeEventListener('paste', pasteHandler));

    // Listen for messages from image editor popup window
    const messageHandler = (event: MessageEvent) => {
      if (event.data && event.data.type === 'imageResize') {
        this.handleImageResizeFromPopup(event.data.width, event.data.height);
      }
    };
    window.addEventListener('message', messageHandler);
    this.listeners.push(() => window.removeEventListener('message', messageHandler));

    // Apply disabled state after view init.
    this.applyCvaDisabled();

    // Initialize toolbar state.
    try { this.updateInlineFormatState(); } catch (e) { }
    try { this.updateColorState(); } catch (e) { }

    // Apply any pending form value written before view init (after contenteditable is set).
    if (this.pendingCvaValue !== null) {
      const v = this.pendingCvaValue;
      this.pendingCvaValue = null;
      this.cvaApplyValue(v);
    }

    // scroll/resize
    const scrollEl = this.pageWrap?.nativeElement;
    if (scrollEl) {
      const scrollHandler = () => {
        if (this.mode === 'doc') this.drawTopRuler();
        this.updateCaretPosition();
        this.updateSelectedImageOverlay();
      };
      scrollEl.addEventListener('scroll', scrollHandler, { passive: true });
      window.addEventListener('resize', scrollHandler);
      this.listeners.push(() => scrollEl.removeEventListener('scroll', scrollHandler));
      this.listeners.push(() => window.removeEventListener('resize', scrollHandler));
    }

    // handle dragging (doc mode only)
    if (this.mode === 'doc') {
      const downTop = (e: MouseEvent) => this.startDrag('top', e);
      // start drag only when pressing the first-line handle area
      const firstLineEl = this.firstLineHandle?.nativeElement;
      if (firstLineEl) {
        firstLineEl.addEventListener('mousedown', downTop);
        this.listeners.push(() => firstLineEl.removeEventListener('mousedown', downTop));
      }
    }

    // global move/up
    const move = (e: MouseEvent) => { this.onDragMove(e); this.onImageMouseMove(e); this.onImageMoveMouseMove(e); this.onTableMouseMove(e); };
    const up = () => { this.endDrag(); this.onImageMouseUp(); this.onImageMoveMouseUp(); this.onTableMouseUp(); };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    this.listeners.push(() => document.removeEventListener('mousemove', move));
    this.listeners.push(() => document.removeEventListener('mouseup', up));

    // delegated click handler for page-level controls (delete button etc.)
    const pageClickHandler = (ev: Event) => {
      try {
        const t = ev.target as HTMLElement | null;
        if (!t || !t.closest) return;
        const btn = t.closest('.page-delete') as HTMLElement | null;
        if (!btn) return;
        const page = btn.closest('.page') as HTMLElement | null;
        if (!page) return;
        // prevent deleting the last page without confirmation
        const pageWrapEl = this.pageWrap?.nativeElement;
        const pages = pageWrapEl ? Array.from(pageWrapEl.querySelectorAll('.page')) as HTMLElement[] : [];
        if (pages.length <= 1) {
          if (!confirm('Delete the only page? A new blank page will be created.')) return;
        }
        this.deletePageElement(page);
      } catch (e) { }
    };
    const pageWrapElGlobal = this.pageWrap?.nativeElement;
    if (pageWrapElGlobal) {
      pageWrapElGlobal.addEventListener('click', pageClickHandler);
      this.listeners.push(() => pageWrapElGlobal.removeEventListener('click', pageClickHandler));
    }

    // delegated interaction handler: ensure clicks inside header place caret after header
    const pageInteractionHandler = (ev: MouseEvent) => {
      try {
        const t = ev.target as HTMLElement | null;
        if (!t || !t.closest) return;
        const page = t.closest('.page') as HTMLElement | null;
        if (!page) return;
        // update current page index
        try {
          const pageWrapEl = pageWrapElGlobal;
          const pages = pageWrapEl ? Array.from(pageWrapEl.querySelectorAll('.page')) as Element[] : [];
          const idx = pages.indexOf(page as Element);
          this.currentPage = idx >= 0 ? idx + 1 : this.currentPage;
        } catch (e) { }

        const editorEl = page.querySelector('.editor') as HTMLElement | null;
        if (!editorEl) return;
        // if clicked inside a header, move caret after header and focus editor
        if (t.closest('.doc-header')) {
          const headerNode = editorEl.querySelector(':scope > .doc-header') as HTMLElement | null || editorEl.querySelector('.doc-header') as HTMLElement | null;
          if (headerNode) {
            if (!headerNode.nextSibling) {
              const p = document.createElement('p'); p.innerHTML = '&nbsp;'; editorEl.appendChild(p);
            }
            try {
              const range = document.createRange();
              range.setStartAfter(headerNode);
              range.collapse(true);
              const sel = window.getSelection();
              sel?.removeAllRanges();
              sel?.addRange(range);
              editorEl.focus();
              this.updateCaretPosition();
            } catch (e) { }
          }
          ev.preventDefault();
          return;
        }
        // otherwise ensure the editor is focused
        if (editorEl.contains(t)) this.focusEditorElement(editorEl);
      } catch (e) { }
    };
    if (pageWrapElGlobal) {
      pageWrapElGlobal.addEventListener('mousedown', pageInteractionHandler);
      this.listeners.push(() => pageWrapElGlobal.removeEventListener('mousedown', pageInteractionHandler));
    }

    // delegated image selection handler
    const imagePointerDown = (ev: MouseEvent) => {
      try {
        const t = ev.target as HTMLElement | null;
        if (!t) return;
        const block = this.findImageBlockFromTarget(t);
        console.log('[Image Click Debug]', {
          target: t.tagName,
          targetClass: t.className,
          foundBlock: !!block,
          blockId: block?.getAttribute('data-image-id'),
          mode: this.mode,
          eventTarget: ev.currentTarget
        });

        if (!block) {
          // clicking elsewhere clears selection
          this.clearSelectedImage();
          return;
        }

        // Prevent default to stop contenteditable from interfering
        ev.preventDefault();
        ev.stopPropagation();

        // If user presses and drags on an already-selected image, start moving it.
        // Ignore drags when resizing/cropping or when the pointer is on a handle/toolbar.
        const isHandle = !!t.closest('.h, .ch, .crop-rect');
        const isToolbar = !!t.closest('.image-toolbar');
        if (
          ev.button === 0 &&
          this.selectedImageBlock === block &&
          !this.isCropping &&
          !this.resizeDrag &&
          !this.cropDrag &&
          !isHandle &&
          !isToolbar
        ) {
          this.startImageMove(ev, block);
          return;
        }

        this.setSelectedImage(block);
      } catch (e) {
        console.error('[Image Click Error]', e);
      }
    };
    if (pageWrapElGlobal) {
      pageWrapElGlobal.addEventListener('mousedown', imagePointerDown, true); // Use capture phase
      this.listeners.push(() => pageWrapElGlobal.removeEventListener('mousedown', imagePointerDown, true));
    }

    // Also add to editor element in compose mode for better click detection
    if (this.mode === 'compose' && inputEl) {
      inputEl.addEventListener('mousedown', imagePointerDown, true);
      this.listeners.push(() => inputEl.removeEventListener('mousedown', imagePointerDown, true));
    }

    // esc cancels crop/selection, arrow keys navigate around images
    const keyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.isCropping) {
        this.cancelCrop();
        return;
      } else if (e.key === 'Escape' && this.showImageOverlay) {
        this.clearSelectedImage();
        return;
      }

      // Handle arrow key navigation when image is selected (MS Word-like behavior)
      if (this.selectedImageBlock && this.showImageOverlay && !this.isCropping) {
        const editorEl = this.getCurrentEditorEl() || this.editor?.nativeElement;
        if (!editorEl) return;

        if (e.key === 'ArrowDown') {
          // Move image down to next line (if Ctrl/Cmd is pressed), otherwise just move cursor
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            try {
              const nextSibling = this.selectedImageBlock.nextSibling;
              if (nextSibling && nextSibling.nextSibling) {
                // Move image after the next element
                editorEl.insertBefore(this.selectedImageBlock, nextSibling.nextSibling);
                this.onEditorInput();
                this.updateSelectedImageOverlay();
              }
            } catch (e) {
              console.warn('[keyDown] Failed to move image down:', e);
            }
          } else {
            // Just move cursor after the image
            e.preventDefault();
            try {
              const range = document.createRange();
              const sel = window.getSelection();

              let nextElement = this.selectedImageBlock.nextSibling as HTMLElement | null;
              if (nextElement && nextElement.tagName === 'P') {
                range.setStart(nextElement, 0);
              } else {
                range.setStartAfter(this.selectedImageBlock);
              }
              range.collapse(true);

              sel?.removeAllRanges();
              sel?.addRange(range);

              this.clearSelectedImage();
              editorEl.focus();
              this.updateCaretPosition();
            } catch (e) {
              console.warn('[keyDown] Failed to move cursor after image:', e);
            }
          }
        } else if (e.key === 'ArrowUp') {
          // Move image up to previous line (if Ctrl/Cmd is pressed), otherwise just move cursor
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            try {
              const prevSibling = this.selectedImageBlock.previousSibling;
              if (prevSibling) {
                // Move image before the previous element
                editorEl.insertBefore(this.selectedImageBlock, prevSibling);
                this.onEditorInput();
                this.updateSelectedImageOverlay();
              }
            } catch (e) {
              console.warn('[keyDown] Failed to move image up:', e);
            }
          } else {
            // Just move cursor before the image
            e.preventDefault();
            try {
              const range = document.createRange();
              const sel = window.getSelection();

              let prevElement = this.selectedImageBlock.previousSibling as HTMLElement | null;
              if (prevElement && prevElement.nodeType === Node.ELEMENT_NODE && prevElement.tagName === 'P') {
                if (prevElement.lastChild) {
                  if (prevElement.lastChild.nodeType === Node.TEXT_NODE) {
                    range.setStart(prevElement.lastChild, prevElement.lastChild.textContent?.length || 0);
                  } else {
                    range.setStartAfter(prevElement.lastChild);
                  }
                } else {
                  range.setStart(prevElement, 0);
                }
              } else {
                range.setStartBefore(this.selectedImageBlock);
              }
              range.collapse(true);

              sel?.removeAllRanges();
              sel?.addRange(range);

              this.clearSelectedImage();
              editorEl.focus();
              this.updateCaretPosition();
            } catch (e) {
              console.warn('[keyDown] Failed to move cursor before image:', e);
            }
          }
        } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          // Left/Right arrows just move cursor
          e.preventDefault();
          try {
            const range = document.createRange();
            const sel = window.getSelection();

            if (e.key === 'ArrowRight') {
              let nextElement = this.selectedImageBlock.nextSibling as HTMLElement | null;
              if (nextElement && nextElement.tagName === 'P') {
                range.setStart(nextElement, 0);
              } else {
                range.setStartAfter(this.selectedImageBlock);
              }
            } else {
              let prevElement = this.selectedImageBlock.previousSibling as HTMLElement | null;
              if (prevElement && prevElement.nodeType === Node.ELEMENT_NODE && prevElement.tagName === 'P') {
                if (prevElement.lastChild) {
                  if (prevElement.lastChild.nodeType === Node.TEXT_NODE) {
                    range.setStart(prevElement.lastChild, prevElement.lastChild.textContent?.length || 0);
                  } else {
                    range.setStartAfter(prevElement.lastChild);
                  }
                } else {
                  range.setStart(prevElement, 0);
                }
              } else {
                range.setStartBefore(this.selectedImageBlock);
              }
            }
            range.collapse(true);

            sel?.removeAllRanges();
            sel?.addRange(range);

            this.clearSelectedImage();
            editorEl.focus();
            this.updateCaretPosition();
          } catch (e) {
            console.warn('[keyDown] Failed to move cursor:', e);
          }
        } else if (e.key === 'Delete' || e.key === 'Backspace') {
          // Delete the selected image
          e.preventDefault();
          try {
            const imageBlock = this.selectedImageBlock;
            this.clearSelectedImage();
            imageBlock?.remove();
            this.onEditorInput();
            editorEl.focus();
          } catch (e) {
            console.warn('[keyDown] Failed to delete image:', e);
          }
        }
      }
    };
    document.addEventListener('keydown', keyDown);
    this.listeners.push(() => document.removeEventListener('keydown', keyDown));

    // Document-level click handler to deselect image when clicking outside editor
    const documentClick = (e: MouseEvent) => {
      if (!this.showImageOverlay) return;

      const target = e.target as HTMLElement | null;
      if (!target) return;

      // Check if click is on image-related elements
      const isImageClick =
        !!target.closest('.image-block') ||
        !!target.closest('.image-selection') ||
        !!target.closest('.image-toolbar') ||
        !!target.closest('.image-frame');

      // If not clicking on image-related elements, clear selection
      if (!isImageClick) {
        this.clearSelectedImage();
      }
    };
    document.addEventListener('mousedown', documentClick);
    this.listeners.push(() => document.removeEventListener('mousedown', documentClick));

    // Keyboard handler: Escape key to deselect image
    const keydownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.selectedImageBlock) {
        e.preventDefault();
        e.stopPropagation();
        this.clearSelectedImage();
      }
      // Arrow keys to exit image selection and continue typing
      if (this.selectedImageBlock && (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        // Only handle if not resizing/cropping
        if (!this.resizeDrag && !this.cropDrag && !this.isCropping) {
          e.preventDefault();
          this.clearSelectedImage();
          // Let the cursor move naturally after clearing
        }
      }
    };
    document.addEventListener('keydown', keydownHandler);
    this.listeners.push(() => document.removeEventListener('keydown', keydownHandler));

    // initial positions
    setTimeout(() => {
      if (this.mode === 'doc') {
        this.drawTopRuler();
        this.positionTopHandleFromIndent();
      }
      this.updateCaretPosition();
      this.normalizeAllImages();
      this.updateSelectedImageOverlay();
    }, 50);
    // count existing pages
    const _wrapEl_for_pageCount = this.getPageWrapEl();
    this.pageCount = _wrapEl_for_pageCount ? _wrapEl_for_pageCount.querySelectorAll('.page').length || 1 : 1;

    // observe editor for footer marker add/remove so we can mirror footer elements and support undo/redo
    try {
      const editorEl = this.editor.nativeElement as HTMLElement;
      this.footerObserver = new MutationObserver((mutations) => this.onEditorMutations(mutations));
      this.footerObserver.observe(editorEl, { childList: true, subtree: true });
    } catch (e) { }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const tokenChanged = !!changes['externalInsertToken'] && this.externalInsertToken !== this.lastExternalInsertToken;
    const textChanged = !!changes['externalInsertText'] && this.externalInsertText !== this.lastExternalInsertText;
    const previewChanged = !!changes['previewHtml'] && this.previewHtml !== null && this.previewHtml !== undefined;

    if (previewChanged) {
      try {
        // Use CVA entrypoint to load incoming HTML into editor (works for compose and doc modes)
        this.writeValue(this.previewHtml || '');
      } catch (e) { console.warn('Failed to apply previewHtml to editor', e); }
    }

    if (tokenChanged || textChanged) {
      this.lastExternalInsertToken = this.externalInsertToken;
      this.lastExternalInsertText = this.externalInsertText;

      const v = this.externalInsertText;
      if (v != null && v !== '') {
        try {
          this.insertTextAtCursor(v);
          // Ensure reactive-forms value is updated even if parent relies only on CVA.
          this.cvaEmitValue();
        } catch (e) { }
      }
    }
  }

  // ---- ControlValueAccessor ----
  writeValue(value: any): void {
    const v = value == null ? '' : String(value);

    // view not initialized yet
    if (!this.editor) {
      this.pendingCvaValue = v;
      return;
    }

    this.cvaApplyValue(v);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled = isDisabled;
    this.applyCvaDisabled();
  }

  private applyCvaDisabled(): void {
    try {
      if (!this.editor?.nativeElement) return;
      this.editor.nativeElement.setAttribute('contenteditable', (!this.cvaDisabled).toString());
      if (this.cvaDisabled) {
        this.editor.nativeElement.classList.add('cva-disabled');
      } else {
        this.editor.nativeElement.classList.remove('cva-disabled');
      }
    } catch (e) { }
  }

  private cvaEmitValue(): void {
    try {
      const el = (this.getCurrentEditorEl() as HTMLElement | null) || this.editor?.nativeElement || null;
      if (!el) return;

      // Emit for non-CVA usage as well.
      try {
        // In compose mode we emit the HTML; in doc mode emit a best-effort HTML snapshot.
        this.valueChange.emit(el.innerHTML ?? '');
      } catch (e) { }

      if (this.mode === 'compose') {
        this.onChange(el.innerHTML ?? '');
        return;
      }

      // doc mode: JSON string model for persistence
      try {
        const model = this.exportDocumentModel();
        this.onChange(JSON.stringify(model));
      } catch (e) {
        // fallback to HTML
        this.onChange(el.innerHTML ?? '');
      }
    } catch (e) { }
  }

  private cvaApplyValue(v: string): void {
    try {
      const el = (this.getCurrentEditorEl() as HTMLElement | null) || this.editor?.nativeElement || null;
      if (!el) return;

      // Guard: when we programmatically set HTML, don't immediately re-emit back into the form.
      this.isApplyingCvaValue = true;

      if (this.mode === 'compose') {
        el.innerHTML = v || '';
        try {
          this.onEditorInput();
        } catch (e) { }
        this.isApplyingCvaValue = false;
        return;
      }

      // doc mode expects JSON model string from exportDocumentModel(); fall back to HTML
      try {
        const parsed = v ? JSON.parse(v) : null;
        if (parsed) {
          this.loadDocumentModel(parsed);
          return;
        }
      } catch (e) {
        // fallthrough
      }

      // last resort: set editor body HTML
      el.innerHTML = v || '';
      try {
        this.onEditorInput();
      } catch (e) { }
      this.isApplyingCvaValue = false;
    } catch (e) { }
  }

  ngOnDestroy() {
    this.listeners.forEach(fn => fn());
    this.listeners = [];
    try { this.footerObserver?.disconnect(); } catch (e) { }
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  toggleSidenav() {
    this.sidenavOpened = !this.sidenavOpened;
  }

  dismissWarning() {
    this.showWarning = false;
  }

  onDone() {
    console.log('Done clicked');
    this.doneClicked.emit();
  }

  /**
   * Persistence helper: exports pages (editor HTML + footer HTML) including image metadata (data-attrs).
   * Consumers can call this via ViewChild.
   */
  exportDocumentModel() {
    const _wrapEl_for_pages = this.getPageWrapEl();
    const pages = _wrapEl_for_pages ? Array.from(_wrapEl_for_pages.querySelectorAll('.page')) as HTMLElement[] : [];
    return {
      version: 1,
      lastHeaderHtml: this.lastHeaderHtml || null,
      lastFooterHtml: this.lastFooterHtml || null,
      pages: pages.map((p) => {
        const editor = p.querySelector('.editor') as HTMLElement | null;
        const footer = p.querySelector('.doc-footer') as HTMLElement | null;
        return {
          editorHtml: editor ? editor.innerHTML : '',
          footerHtml: footer ? footer.outerHTML : null
        };
      })
    };
  }

  /**
   * Persistence helper: loads pages previously returned by exportDocumentModel().
   */
  loadDocumentModel(model: any) {
    if (!model || !Array.isArray(model.pages)) return;

    try {
      this.lastHeaderHtml = model.lastHeaderHtml || undefined;
      this.lastFooterHtml = model.lastFooterHtml || undefined;
    } catch (e) { }

    // clear existing pages
    try {
      const wrap = this.getPageWrapEl();
      const existing = wrap ? Array.from(wrap.querySelectorAll('.page')) as HTMLElement[] : [];
      existing.forEach((p) => p.remove());
    } catch (e) { }

    // rebuild pages
    for (const pageModel of model.pages) {
      const page = this.createPageElement();
      const editor = page.querySelector('.editor') as HTMLElement | null;
      if (editor) {
        editor.innerHTML = pageModel?.editorHtml || '';
      }

      // attach footer if present
      if (pageModel?.footerHtml) {
        try {
          const tmp = document.createElement('div');
          tmp.innerHTML = String(pageModel.footerHtml);
          const node = tmp.firstElementChild as HTMLElement | null;
          if (node) {
            // ensure positioning consistent
            node.classList.add('doc-footer');
            try {
              node.style.position = 'absolute';
              node.style.left = (page.style.paddingLeft && page.style.paddingLeft !== '') ? page.style.paddingLeft : '24px';
              node.style.right = (page.style.paddingRight && page.style.paddingRight !== '') ? page.style.paddingRight : '24px';
              node.style.bottom = '24px';
              node.style.boxSizing = 'border-box';
            } catch (e) { }
            page.appendChild(node);
          }
        } catch (e) { }
      }

      const _wrap_for_append = this.getPageWrapEl();
      if (_wrap_for_append) _wrap_for_append.appendChild(page);
    }

    const _wrap_for_count = this.getPageWrapEl();
    this.pageCount = _wrap_for_count ? _wrap_for_count.querySelectorAll('.page').length || 1 : 1;
    this.currentPage = Math.min(this.currentPage, this.pageCount);
    this.normalizeAllImages();
    this.onEditorInput();
  }



  onEditorInput(evt?: Event) {
    const el = this.editor?.nativeElement;
    if (!el) return;

    const sel = window.getSelection();
    const anchor = sel?.anchorNode as HTMLElement | null;

    // if editing inside media element → do nothing
    if (anchor && ['IMG', 'VIDEO', 'AUDIO'].includes(anchor.nodeName)) {
      return;
    }

    // TEXT COUNT + STATE SYNC (keep as is)
    const text = el.innerText || '';
    this.chars = text.replace(/\s/g, '').length;
    const words = text.trim().split(/\s+/).filter(w => w.length > 0);
    this.words = words.length;

    if (this.mode === 'doc') this.drawTopRuler();

    // CRITICAL: Normalize images whenever content changes to ensure frame sizes are applied
    try { this.normalizeAllImages(); } catch { }

    try { this.updateInlineFormatState(); } catch { }
    try { this.updateColorState(); } catch { }
    try {
      if (!this.isApplyingCvaValue) {
        this.cvaEmitValueFromEl(el);
      }
    } catch { }
  }

  onEditorMouseDown(event: MouseEvent) {
    console.log('[onEditorMouseDown]', event.target);
    const target = event.target as HTMLElement;
    if (!target) return;

    // Check if clicking on an image or inside an image-block
    const imageBlock = target.closest('.image-block') as HTMLElement | null;
    if (imageBlock) {
      console.log('[onEditorMouseDown] Found image-block:', imageBlock.getAttribute('data-image-id'));
      event.preventDefault();
      event.stopPropagation();
      this.setSelectedImage(imageBlock);
      this.deselectTable();
      return;
    }

    // Check if clicking on a table
    const table = target.closest('table') as HTMLTableElement | null;
    if (table) {
      console.log('[onEditorMouseDown] Found table');
      this.selectTable(table);
      this.clearSelectedImage();
      return;
    }

    // Clicked outside image/table - clear selection and place cursor
    this.handleEditorClick(event);
  }

  onComposePageMouseDown(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target) return;

    // Only handle if clicking directly on the compose-page element (not on children)
    if (target.classList.contains('compose-page')) {
      this.handleEditorClick(event);
    }
  }

  onComposeWrapMouseDown(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target) return;

    // Only handle if clicking directly on the compose-wrap element (not on children)
    if (target.classList.contains('compose-wrap')) {
      this.handleEditorClick(event);
    }
  }

  private handleEditorClick(event: MouseEvent) {
    console.log('[handleEditorClick] Clearing selection and placing cursor');

    const editorEl = this.getCurrentEditorEl() || this.editor?.nativeElement;
    if (!editorEl) return;

    // Clear image selection first
    if (this.selectedImageBlock) {
      this.clearSelectedImage();
    }

    // Now place cursor where user clicked or at the end
    try {
      const range = document.createRange();
      const sel = window.getSelection();

      // Check if editor is empty
      if (!editorEl.textContent || editorEl.textContent.trim() === '') {
        // Editor is empty - create a paragraph
        const p = document.createElement('p');
        p.innerHTML = '<br>';
        editorEl.appendChild(p);
        range.setStart(p, 0);
        range.collapse(true);
        sel?.removeAllRanges();
        sel?.addRange(range);
        editorEl.focus();
        return;
      }

      // Try to place cursor at click position using browser APIs
      let caretRange: Range | null = null;

      // Try standard API first (Firefox/modern browsers)
      if (typeof (document as any).caretPositionFromPoint === 'function') {
        const caretPos = (document as any).caretPositionFromPoint(event.clientX, event.clientY);
        if (caretPos && caretPos.offsetNode) {
          range.setStart(caretPos.offsetNode, caretPos.offset);
          range.collapse(true);
          caretRange = range;
        }
      }
      // Fallback to WebKit API (Chrome/Safari)
      else if (typeof (document as any).caretRangeFromPoint === 'function') {
        caretRange = (document as any).caretRangeFromPoint(event.clientX, event.clientY);
      }

      if (caretRange && editorEl.contains(caretRange.startContainer)) {
        // Valid range within editor
        sel?.removeAllRanges();
        sel?.addRange(caretRange);
        editorEl.focus();
        console.log('[handleEditorClick] Cursor placed at click position');
      } else {
        // Couldn't place at click position - place at end of editor
        const lastChild = editorEl.lastChild;
        if (lastChild) {
          if (lastChild.nodeType === Node.TEXT_NODE) {
            range.setStart(lastChild, lastChild.textContent?.length || 0);
          } else if (lastChild.nodeType === Node.ELEMENT_NODE) {
            range.setStart(lastChild, (lastChild as Element).childNodes.length);
          } else {
            range.setStartAfter(lastChild);
          }
        } else {
          range.selectNodeContents(editorEl);
        }
        range.collapse(false);
        sel?.removeAllRanges();
        sel?.addRange(range);
        editorEl.focus();
        console.log('[handleEditorClick] Cursor placed at end of editor');
      }
    } catch (e) {
      console.warn('[handleEditorClick] Failed to place cursor:', e);
      // Fallback - just focus
      const editorEl = this.getCurrentEditorEl() || this.editor?.nativeElement;
      if (editorEl) editorEl.focus();
    }
  }



  private updateColorState(): void {
    try {
      // Only update when selection/caret is inside this editor.
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) {
        this.zone.run(() => {
          this.currentTextColor = null;
          this.currentHighlightColor = null;
        });
        return;
      }

      const range = sel.getRangeAt(0);
      const node = range.startContainer as Node | null;
      const host = (node && (node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement)) as HTMLElement | null;
      const editorHost =
        (host?.closest?.('.editor') as HTMLElement | null) ||
        (host?.closest?.('.doc-header') as HTMLElement | null) ||
        (host?.closest?.('.doc-footer') as HTMLElement | null);

      const _wrap_for_contains = this.getPageWrapEl();
      if (!editorHost || (_wrap_for_contains && !_wrap_for_contains.contains(editorHost))) {
        this.zone.run(() => {
          this.currentTextColor = null;
          this.currentHighlightColor = null;
        });
        return;
      }

      const fore = this.normalizeCssColorSafe(this.queryCommandValueSafe('foreColor'));
      // Some browsers use backColor instead of hiliteColor.
      const hiRaw = this.queryCommandValueSafe('hiliteColor') || this.queryCommandValueSafe('backColor');
      const hi = this.normalizeCssColorSafe(hiRaw);

      if (fore === this.currentTextColor && hi === this.currentHighlightColor) return;

      this.zone.run(() => {
        this.currentTextColor = fore;
        this.currentHighlightColor = hi;
      });
    } catch (e) {
      // ignore
    }
  }

  private queryCommandValueSafe(command: string): string {
    try {
      const v = document.queryCommandValue(command);
      return (v == null ? '' : String(v));
    } catch (e) {
      return '';
    }
  }

  private normalizeCssColorSafe(value: string): string {
    try {
      const v = (value || '').toString().trim().toLowerCase();
      if (!v) return '';

      // Already hex.
      if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/.test(v)) {
        if (v.length === 4) {
          return '#' + v[1] + v[1] + v[2] + v[2] + v[3] + v[3];
        }
        return v;
      }

      // rgb/rgba
      const m = v.match(/^rgba?\((\s*\d+\s*),(\s*\d+\s*),(\s*\d+\s*)(?:,\s*([\d.]+)\s*)?\)$/);
      if (m) {
        const r = Math.max(0, Math.min(255, parseInt(m[1], 10) || 0));
        const g = Math.max(0, Math.min(255, parseInt(m[2], 10) || 0));
        const b = Math.max(0, Math.min(255, parseInt(m[3], 10) || 0));
        const toHex = (n: number) => n.toString(16).padStart(2, '0');
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
      }

      return v;
    } catch (e) {
      return (value || '').toString().trim().toLowerCase();
    }
  }

  isTextColorSelected(color: string): boolean {
    return this.normalizeCssColorSafe(color) !== '' && this.normalizeCssColorSafe(color) === (this.currentTextColor || '');
  }

  isHighlightColorSelected(color: string): boolean {
    return this.normalizeCssColorSafe(color) !== '' && this.normalizeCssColorSafe(color) === (this.currentHighlightColor || '');
  }

  private updateInlineFormatState(): void {
    try {
      // Only show active state when selection/caret is inside this editor.
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) {
        this.zone.run(() => {
          this.isBoldActive = false;
          this.isItalicActive = false;
          this.isUnderlineActive = false;
        });
        return;
      }

      const range = sel.getRangeAt(0);
      const node = range.startContainer as Node | null;
      const host = (node && (node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement)) as HTMLElement | null;
      const editorHost =
        (host?.closest?.('.editor') as HTMLElement | null) ||
        (host?.closest?.('.doc-header') as HTMLElement | null) ||
        (host?.closest?.('.doc-footer') as HTMLElement | null);

      // If selection isn't in our editor surface, don't show any formatting state.
      const _wrap_for_contains = this.getPageWrapEl();
      if (!editorHost || (_wrap_for_contains && !_wrap_for_contains.contains(editorHost))) {
        this.zone.run(() => {
          this.isBoldActive = false;
          this.isItalicActive = false;
          this.isUnderlineActive = false;
        });
        return;
      }

      let bold = false;
      let italic = false;
      let underline = false;
      try { bold = !!document.queryCommandState('bold'); } catch (e) { }
      try { italic = !!document.queryCommandState('italic'); } catch (e) { }
      try { underline = !!document.queryCommandState('underline'); } catch (e) { }

      // Detect current heading tag
      let currentHeadingTag = 'P';
      try {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
          let node: Node | null = sel.getRangeAt(0).commonAncestorContainer;
          if (node.nodeType === Node.TEXT_NODE) {
            node = node.parentElement;
          }

          // Walk up the tree to find a heading element
          while (node && node !== editorHost) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const tagName = (node as HTMLElement).tagName;
              if (['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'P'].includes(tagName)) {
                currentHeadingTag = tagName;
                break;
              }
            }
            node = node.parentElement;
          }
        }
      } catch (e) { }

      // Avoid extra change detection work when nothing changed.
      if (bold === this.isBoldActive && italic === this.isItalicActive && underline === this.isUnderlineActive && currentHeadingTag === this.selectedHeadingTag) {
        return;
      }

      this.zone.run(() => {
        this.isBoldActive = bold;
        this.isItalicActive = italic;
        this.isUnderlineActive = underline;
        this.selectedHeadingTag = currentHeadingTag;
      });
    } catch (e) {
      // ignore
    }
  }

  private cvaEmitValueFromEl(el: HTMLElement): void {
    try {
      // Emit for non-CVA usage as well.
      try {
        this.valueChange.emit(el.innerHTML ?? '');
      } catch (e) { }

      if (this.mode === 'compose') {
        this.onChange(el.innerHTML ?? '');
        return;
      }

      // doc mode: JSON string model for persistence
      try {
        const model = this.exportDocumentModel();
        this.onChange(JSON.stringify(model));
      } catch (e) {
        this.onChange(el.innerHTML ?? '');
      }
    } catch (e) { }
  }

  private captureLastSelection() {
    try {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      const startNode = range.startContainer as Node | null;
      const host = (startNode && (startNode.nodeType === Node.ELEMENT_NODE ? (startNode as Element) : startNode.parentElement)) as HTMLElement | null;
      if (!host) return;

      const editorHost =
        (host.closest('.editor') as HTMLElement | null) ||
        (host.closest('.doc-header') as HTMLElement | null) ||
        (host.closest('.doc-footer') as HTMLElement | null);
      if (!editorHost) return;

      // clone to avoid mutation by browser as focus changes
      this.lastSelectionRange = range.cloneRange();
      this.lastSelectionHost = editorHost;
    } catch (e) { }
  }

  private focusAndRestoreSelectionFor(host: HTMLElement) {
    try {
      host.focus();
    } catch (e) { }
    try {
      const sel = window.getSelection();
      if (!sel) return;

      // Restore only if our saved range belongs to this host.
      if (this.lastSelectionRange && this.lastSelectionHost === host) {
        try {
          sel.removeAllRanges();
          sel.addRange(this.lastSelectionRange);
          return;
        } catch (e) { }
      }

      // Otherwise place caret at end of host.
      try {
        const r = document.createRange();
        r.selectNodeContents(host);
        r.collapse(false);
        sel.removeAllRanges();
        sel.addRange(r);
      } catch (e) { }
    } catch (e) { }
  }

  focusEditor() {
    const editorEl = this.getCurrentEditorEl();
    if (editorEl) this.focusAndRestoreSelectionFor(editorEl);
    else {
      try { this.editor.nativeElement.focus(); } catch (e) { }
    }
  }

  exec(command: string, value: string | null = null) {
    // Restore caret/selection into the editor before applying a command.
    // Important: color menus steal focus, so fall back to lastSelectionHost.
    const editorEl = this.getCurrentEditorEl();
    const host = editorEl || this.lastSelectionHost || (this.editor?.nativeElement as HTMLElement | null);
    if (host) this.focusAndRestoreSelectionFor(host);
    else this.focusEditor();
    try {
      document.execCommand(command, false, value);
      this.onEditorInput();
    } catch (e) {
      console.warn('execCommand failed', command, e);
    }
  }

  undo() { this.exec('undo'); }
  redo() { this.exec('redo'); }
  cut() { this.exec('cut'); }
  copy() { this.exec('copy'); }
  paste() { this.exec('paste'); }

  toggleBold() { this.exec('bold'); }
  toggleItalic() { this.exec('italic'); }
  toggleUnderline() { this.exec('underline'); }

  alignLeft() { this.exec('justifyLeft'); }
  alignCenter() { this.exec('justifyCenter'); }
  alignRight() { this.exec('justifyRight'); }

  insertUnorderedList() { this.exec('insertUnorderedList'); }
  insertOrderedList() { this.exec('insertOrderedList'); }

  getHeadingLabel(tag: string): string {
    const heading = this.headingStyles.find(h => h.tag === tag);
    return heading ? heading.label : 'Normal';
  }

  setHeadingStyle(tag: string) {
    try {
      const upper = (tag || 'P').toUpperCase();
      this.selectedHeadingTag = upper;

      const host = (this.getCurrentEditorEl() as HTMLElement | null) || this.lastSelectionHost;

      if (host) {
        this.focusAndRestoreSelectionFor(host);
      }

      const sel = window.getSelection();
      if (!host || !sel || sel.rangeCount === 0) {
        return;
      }

      const range = sel.getRangeAt(0);
      const tagName = upper === 'P' ? 'p' : upper.toLowerCase();

      // Get the current block element
      let currentBlock = range.commonAncestorContainer;
      if (currentBlock.nodeType === Node.TEXT_NODE) {
        currentBlock = currentBlock.parentElement!;
      }

      // Find the actual block element (p, h1, h2, etc.)
      while (currentBlock && currentBlock !== host && !this.isBlockElement(currentBlock as HTMLElement)) {
        currentBlock = currentBlock.parentElement!;
      }

      if (currentBlock && currentBlock !== host && this.isBlockElement(currentBlock as HTMLElement)) {
        // Replace the existing block with new heading tag
        const newElement = document.createElement(tagName);
        newElement.innerHTML = (currentBlock as HTMLElement).innerHTML;

        // Strip inline font-sizes from children so the heading CSS controls the size (1em default)
        try {
          const allChildren = newElement.querySelectorAll('*');
          allChildren.forEach((el) => {
            if (el instanceof HTMLElement) {
              el.style.fontSize = '';
              el.style.removeProperty('font-size');
            }
            if (el.tagName === 'FONT') {
              el.removeAttribute('size');
            }
          });
        } catch (e) { }

        (currentBlock as HTMLElement).replaceWith(newElement);

        // Restore selection inside new element
        try {
          const newRange = document.createRange();
          newRange.selectNodeContents(newElement);
          newRange.collapse(false);
          sel.removeAllRanges();
          sel.addRange(newRange);
          this.lastSelectionRange = newRange.cloneRange();
          this.lastSelectionHost = host;
        } catch (e) { }
      } else {
        // Use formatBlock for new block creation
        document.execCommand('formatBlock', false, tagName);

        // Update selection tracking
        try {
          if (sel.rangeCount > 0) {
            this.lastSelectionRange = sel.getRangeAt(0).cloneRange();
            this.lastSelectionHost = host;
          }
        } catch (e) { }
      }

      this.onEditorInput();
      return;
    } catch (e) {
      console.error('Error setting heading style:', e);
      return;
    }
  }

  private isBlockElement(element: HTMLElement): boolean {
    const blockTags = ['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE'];
    return blockTags.includes(element.tagName);
  }

  private applyInlineHeadingToRange(host: HTMLElement, range: Range, upperTag: string): boolean {
    try {
      // Ensure range is within host
      if (!host.contains(range.commonAncestorContainer)) return false;

      const tag = (upperTag || 'P').toUpperCase();
      const level = tag === 'P' ? 'p' : tag.toLowerCase();

      const span = document.createElement('span');
      span.className = `doc-inline-heading doc-inline-heading-${level}`;
      span.setAttribute('data-inline-heading', tag);

      const contents = range.extractContents();
      span.appendChild(contents);
      range.insertNode(span);

      // Place caret AFTER the styled span so new typing stays normal.
      try {
        const sel = window.getSelection();
        if (sel) {
          const r = document.createRange();
          r.setStartAfter(span);
          r.collapse(true);
          sel.removeAllRanges();
          sel.addRange(r);
          this.lastSelectionRange = r.cloneRange();
          this.lastSelectionHost = host;
        }
      } catch (e) { }

      return true;
    } catch (e) {
      return false;
    }
  }

  private findBlockElementForRange(host: HTMLElement, range: Range): HTMLElement | null {
    try {
      const blockTags = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6']);
      let el = (range.startContainer.nodeType === Node.ELEMENT_NODE
        ? (range.startContainer as HTMLElement)
        : (range.startContainer.parentElement as HTMLElement | null));

      while (el && el !== host) {
        if (blockTags.has(el.tagName)) {
          // Avoid turning layout containers into headings.
          if (el.classList.contains('editor') || el.classList.contains('doc-header') || el.classList.contains('doc-footer')) {
            return null;
          }
          return el;
        }
        el = el.parentElement;
      }

      // If host itself is a block (rare), allow P fallback.
      if (el === host) {
        const first = host.querySelector('p, h1, h2, h3, h4, h5, h6, div');
        return first as HTMLElement | null;
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  private replaceElementTag(el: HTMLElement, upperTag: string): HTMLElement {
    const tag = (upperTag || 'P').toUpperCase();
    if (el.tagName === tag) return el;

    const next = document.createElement(tag.toLowerCase());
    try {
      // Preserve inline styles so heading change doesn't unexpectedly alter font settings.
      (next as HTMLElement).style.cssText = (el as HTMLElement).style.cssText;
    } catch (e) { }

    while (el.firstChild) next.appendChild(el.firstChild);
    el.parentNode?.replaceChild(next, el);
    return next as HTMLElement;
  }

  setFont(font: string) { this.applyFont(font); }

  // more robust font application: apply to selection only
  applyFont(font: string) {
    this.selectedFont = font;
    const editor = this.getCurrentEditorEl();
    const host = editor || this.lastSelectionHost;

    if (host) {
      this.focusAndRestoreSelectionFor(host);
    }

    const sel = window.getSelection();
    const cssValue = font.indexOf(' ') >= 0 ? `"${font}"` : font;

    // Only apply font if text is selected
    if (sel && sel.rangeCount > 0 && !sel.isCollapsed) {
      try { document.execCommand('fontName', false, font); } catch (e) { }
      // normalize any <font face=> tags inserted by execCommand
      if (editor) {
        const fontTags = Array.from(editor.querySelectorAll('font')) as HTMLElement[];
        fontTags.forEach(f => {
          try {
            const parent = f.parentElement;
            // move children out and replace with a span that has style
            const span = document.createElement('span');
            span.style.fontFamily = font;
            while (f.firstChild) span.appendChild(f.firstChild);
            if (parent) parent.replaceChild(span, f);
          } catch (e) { }
        });
      }
    }
  }

  setSize(size: string) {
    this.selectedSize = size;
    const editor = this.getCurrentEditorEl();
    const host = editor || this.lastSelectionHost;

    if (host) {
      this.focusAndRestoreSelectionFor(host);
    }

    const sel = window.getSelection();

    // Try to use saved selection if current selection is collapsed but we have a saved range
    let useRange: Range | null = null;
    if (sel && sel.rangeCount > 0 && !sel.isCollapsed) {
      useRange = sel.getRangeAt(0);
    } else if (this.lastSelectionRange && !this.lastSelectionRange.collapsed) {
      // Use saved selection from before toolbar interaction
      useRange = this.lastSelectionRange;
    }

    // If we have a text range (either current or saved), wrap selection in span with font-size
    if (useRange && !useRange.collapsed) {
      try {
        // Restore the range first
        if (sel) {
          sel.removeAllRanges();
          sel.addRange(useRange);
        }

        // Check if the selection is already within a single span element
        let existingSpan: HTMLElement | null = null;
        const startContainer = useRange.startContainer;
        const endContainer = useRange.endContainer;

        // If both start and end are in the same span, use that span
        if (startContainer === endContainer || startContainer.parentElement === endContainer.parentElement) {
          const parent = startContainer.nodeType === Node.TEXT_NODE
            ? startContainer.parentElement
            : startContainer as HTMLElement;

          if (parent && parent.tagName === 'SPAN') {
            existingSpan = parent;
          }
        }

        if (existingSpan) {
          // Just update the font-size of the existing span
          existingSpan.style.fontSize = size + 'px';
          console.log('[setSize] Updated existing span font-size:', size + 'px');
        } else {
          // Create a new span and wrap the selected content
          const span = document.createElement('span');
          span.style.fontSize = size + 'px';

          const contents = useRange.extractContents();
          span.appendChild(contents);
          useRange.insertNode(span);

          // Restore selection after the span
          const newRange = document.createRange();
          newRange.selectNodeContents(span);
          newRange.collapse(false);
          if (sel) {
            sel.removeAllRanges();
            sel.addRange(newRange);
          }
          console.log('[setSize] Created new span with font-size:', size + 'px');
        }
      } catch (e) {
        console.error('Error applying font size:', e);
      }
      this.onEditorInput();
      return;
    }

    // No selection: apply font-size to current block element (div, p, etc.)
    try {
      if (editor) {
        editor.focus();
      }

      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        let node = range.startContainer;

        // Get the element node
        if (node.nodeType === Node.TEXT_NODE) {
          node = node.parentElement!;
        }

        // Find the block element (div, p, etc.)
        let blockElement: HTMLElement | null = null;
        let current = node as HTMLElement;

        while (current && current !== editor) {
          const tagName = current.tagName;
          if (['DIV', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'BLOCKQUOTE'].includes(tagName)) {
            blockElement = current;
            break;
          }
          current = current.parentElement!;
        }

        // Apply font-size to the block element
        if (blockElement) {
          blockElement.style.fontSize = size + 'px';
          console.log('[setSize] Applied font-size to block element:', blockElement.tagName, size + 'px');
        } else {
          // No block element found, create a span with font-size at cursor
          const span = document.createElement('span');
          span.style.fontSize = size + 'px';
          span.appendChild(document.createTextNode('\u200B'));

          range.deleteContents();
          range.insertNode(span);

          const newRange = document.createRange();
          newRange.setStart(span.firstChild!, 1);
          newRange.setEnd(span.firstChild!, 1);
          sel.removeAllRanges();
          sel.addRange(newRange);

          console.log('[setSize] Created span with font-size:', size + 'px');
        }
      }
    } catch (e) {
      console.error('Error applying font size:', e);
    }
  }

  increaseSize() {
    const i = this.sizes.indexOf(this.selectedSize);
    if (i < this.sizes.length - 1) this.setSize(this.sizes[i + 1]);
  }

  decreaseSize() {
    const i = this.sizes.indexOf(this.selectedSize);
    if (i > 0) this.setSize(this.sizes[i - 1]);
  }

  setColor(color: string) {
    this.exec('foreColor', color);
    try { this.updateColorState(); } catch (e) { }
  }

  // apply background/highlight color
  setHighlight(color: string) {
    this.exec('hiliteColor', color);
    try { this.updateColorState(); } catch (e) { }
  }

  setFontFamily(font: string) {
    this.selectedFont = font;
    const editor = this.getCurrentEditorEl();
    const host = editor || this.lastSelectionHost;

    if (host) {
      this.focusAndRestoreSelectionFor(host);
    }

    const sel = window.getSelection();

    // Only apply font if text is selected
    if (sel && sel.rangeCount > 0 && !sel.isCollapsed) {
      // Wrap font name in quotes if it contains spaces
      const fontValue = font.indexOf(' ') >= 0 ? `"${font}"` : font;

      try { document.execCommand('fontName', false, fontValue); } catch (e) { }

      // normalize any <font face=> tags inserted by execCommand
      if (editor) {
        const fontTags = Array.from(editor.querySelectorAll('font')) as HTMLElement[];
        fontTags.forEach(f => {
          try {
            const parent = f.parentElement;
            // move children out and replace with a span that has style
            const span = document.createElement('span');
            span.style.fontFamily = fontValue;
            while (f.firstChild) span.appendChild(f.firstChild);
            if (parent) parent.replaceChild(span, f);
          } catch (e) { }
        });
      }
      this.onEditorInput();
    }
  }

  insertQuickPart(html: string) {
    const editorEl = this.getCurrentEditorEl();
    if (editorEl) {
      this.insertHtmlIntoEditor(editorEl, html, 'afterHeader');
    } else {
      this.exec('insertHTML', html);
    }
  }

  fileAttr = 'Choose File';
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  insertImage(event: Event) {
    const input = event.target as HTMLInputElement;

    if (!input.files || input.files.length === 0) {
      this.fileAttr = 'Choose File';
      return;
    }

    const file = input.files[0];
    this.fileAttr = file.name;

    try {
      const data = new FormData();
      const safeName = (file.name || 'file')
        .replace(/\s/g, '_')
        .replace(/\(/g, '_')
        .replace(/\)/g, '_');
      data.append('files', file, safeName);

      const cid = localStorage.getItem(Constant.enterPriseKey) || '';
      const location = 'calsoftgroup.com';

      this.fileUploadService.normalUploadFiles(data, 'gmail', location).subscribe({
        next: (res: any) => {
          this.data1 = res;
          if (res && res.status_code === '500') {
            this.snackBar.open(res.status_message || 'An error occurred during upload', '×', { panelClass: 'error', verticalPosition: 'bottom', duration: 3000 });
            this.uploadInProgress = false;
            return;
          }
          const fileName = res?.productEntityMediaGallery?.[0]?.fileName;
          if (!fileName) {
            console.warn('File upload succeeded but no fileName returned');
            return;
          }

          let fileUrl = environment.commonImageApi + 'gmail/' + fileName;
          if (cid) fileUrl += '&cid=' + cid;

          const mimeType = file.type || '';
          if (mimeType.startsWith('image/')) {
            this.insertMediaToEditor(fileUrl, 'image');
          } else if (mimeType.includes('pdf')) {
            this.insertMediaToEditor(fileUrl, 'pdf');
          } else {
            this.insertMediaToEditor(fileUrl, 'document');
          }
        },
        error: (err) => {
          console.error('[insertImage] Upload failed:', err);
          this.snackBar.open('Failed to upload file. Please try again.', '×', { panelClass: 'error', verticalPosition: 'bottom', duration: 3000 });
        }
      });
    } catch (e) {
      console.error('File upload error:', e);
      this.snackBar.open('Failed to upload file. Please try again.', '×', { panelClass: 'error', verticalPosition: 'bottom', duration: 3000 });
    }

    input.value = '';
    this.fileAttr = 'Choose File';
  }

  /** Export the current compose/editor area as a PDF file. */
  exportEditorPdf(): void {
    try {
      // Choose the element to export: full page preview in 'doc' mode, otherwise the editable area
      let sourceEl: HTMLElement | null = null;
      if (this.mode === 'doc') {
        sourceEl = this.pageEl?.nativeElement || this.pageWrap?.nativeElement || this.editor?.nativeElement || (document.querySelector('.compose-editor') as HTMLElement | null);
      } else {
        sourceEl = (this.getCurrentEditorEl() as HTMLElement | null) || this.editor?.nativeElement || (document.querySelector('.compose-editor') as HTMLElement | null);
      }

      if (!sourceEl) {
        console.warn('[exportEditorPdf] No editor/page element found to export');
        return;
      }

      // Clone the node so we don't disturb the live editor, and ensure background is white
      const clone = sourceEl.cloneNode(true) as HTMLElement;
      clone.style.background = clone.style.background || '#ffffff';
      clone.style.boxSizing = 'border-box';

      // Wrap cloned content in a sized container so html2canvas measures correctly
      const wrapper = document.createElement('div');
      wrapper.style.width = sourceEl.clientWidth + 'px';
      wrapper.style.background = '#ffffff';
      wrapper.style.padding = '0';
      wrapper.style.boxSizing = 'border-box';
      wrapper.appendChild(clone);

      // Append to body temporarily so computed styles and external resources render
      document.body.appendChild(wrapper);

      const opt = {
        margin: 0.5,
        filename: `document-preview.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
      } as any;

      const task = (html2pdf as any)().set(opt).from(wrapper).save();
      // Remove temporary wrapper when done (html2pdf returns a promise in modern builds)
      if (task && typeof task.then === 'function') {
        task.finally(() => {
          try { document.body.removeChild(wrapper); } catch (e) { }
        });
      } else {
        // Fallback: remove after short timeout
        setTimeout(() => { try { document.body.removeChild(wrapper); } catch (e) { } }, 2000);
      }
    } catch (err) {
      console.error('[exportEditorPdf] Error exporting editor to PDF', err);
    }
  }

  // --- Image helpers / behaviors ---

  private generateImageId() {
    return 'img_' + Math.random().toString(36).slice(2, 9) + '_' + Date.now();
  }

  private findImageBlockFromTarget(target: HTMLElement): HTMLElement | null {
    try {
      return target.closest('.image-block') as HTMLElement | null;
    } catch (e) {
      return null;
    }
  }

  private findImageBlockById(id: string): HTMLElement | null {
    try {
      // In compose mode, search within editor element
      if (this.mode === 'compose') {
        const editorEl = this.getCurrentEditorEl() || this.editor?.nativeElement;
        if (editorEl) {
          const block = editorEl.querySelector(`.image-block[data-image-id="${id}"]`) as HTMLElement | null;
          if (block) return block;
        }
      }
      // In doc mode (or fallback), search in pageWrap
      return this.pageWrap?.nativeElement?.querySelector(`.image-block[data-image-id="${id}"]`) as HTMLElement | null;
    } catch (e) {
      console.error('[findImageBlockById] Error:', e);
      return null;
    }
  }

  private getActiveRegion(): DocRegion {
    try {
      const sel = window.getSelection();
      const n = sel?.anchorNode as Node | null;
      const el = (n && (n.nodeType === Node.ELEMENT_NODE ? (n as Element) : n.parentElement)) as HTMLElement | null;
      if (!el) return 'body';
      if (el.closest('.doc-footer')) return 'footer';
      if (el.closest('.doc-header')) return 'header';
      return 'body';
    } catch (e) {
      return 'body';
    }
  }

  private getCurrentPageEl(): HTMLElement | null {
    try {
      try {
        const wrap = this.getPageWrapEl();
        const pages = wrap ? Array.from(wrap.querySelectorAll('.page')) as HTMLElement[] : [];
        const idx = Math.max(0, Math.min(pages.length - 1, this.currentPage - 1));
        return pages[idx] || (this.pageEl?.nativeElement as HTMLElement);
      } catch (e) {
        return this.pageEl?.nativeElement as HTMLElement | null;
      }
    } catch (e) {
      return null;
    }
  }

  private insertHtmlIntoActiveRegion(html: string) {
    const region = this.getActiveRegion();
    const page = this.getCurrentPageEl();
    const editorEl = this.getCurrentEditorEl();
    if (!page) return;

    if (region === 'footer') {
      const footer = page.querySelector('.doc-footer') as HTMLElement | null;
      if (footer) {
        this.insertHtmlIntoContainer(footer, html);
        return;
      }
      // fallback to body if no footer exists
    }

    if (editorEl) {
      if (region === 'header') this.insertHtmlIntoContainer(editorEl, html);
      else this.insertHtmlIntoEditor(editorEl, html, 'afterHeader');
    } else {
      this.exec('insertHTML', html);
    }
  }

  private insertHtmlIntoContainer(container: HTMLElement, html: string) {
    try {
      container.focus();
      const sel = window.getSelection();
      let range: Range | null = null;
      if (sel && sel.rangeCount > 0) range = sel.getRangeAt(0);
      if (!range) {
        range = document.createRange();
        range.selectNodeContents(container);
        range.collapse(false);
      }
      sel?.removeAllRanges();
      sel?.addRange(range);
      document.execCommand('insertHTML', false, html);
      this.onEditorInput();
      this.updateCaretPosition();
    } catch (e) {
      try {
        container.insertAdjacentHTML('beforeend', html);
      } catch (e2) { }
    }
  }

  private getMaxContentWidthFor(imageBlock: HTMLElement): number {
    try {
      const container = (imageBlock.closest('.editor') as HTMLElement | null)
                     || (imageBlock.closest('.doc-footer') as HTMLElement | null)
                     || (imageBlock.closest('.compose-editor') as HTMLElement | null)
                     || (this.editor?.nativeElement as HTMLElement | null);
      let width = container?.clientWidth || container?.offsetWidth || 0;
      if (width < 300) {
        const pageWrap = this.pageWrap?.nativeElement;
        if (pageWrap && pageWrap.clientWidth > 300) {
          width = pageWrap.clientWidth;
        } else {
          width = 650;
        }
      }
      return width;
    } catch (e) {
      return 650;
    }
  }

  private setImageBlockSize(block: HTMLElement, width: number, height: number) {
    const minW = 30;
    const minH = 30;
    const maxW = this.getMaxContentWidthFor(block);
    const w = Math.max(minW, Math.min(Math.round(width), Math.round(maxW)));
    const h = Math.max(minH, Math.round(height));
    try {
      block.setAttribute('data-image-w', String(w));
      block.setAttribute('data-image-h', String(h));
      const frame = block.querySelector('.image-frame') as HTMLElement | null;
      const img = block.querySelector('.image-content') as HTMLImageElement | null;
      if (frame) {
        frame.style.width = `${w}px`;
        frame.style.maxWidth = '100%';
        frame.style.height = `${h}px`;
        frame.style.maxHeight = '100%';
        console.log('[setImageBlockSize] Set frame size:', { w, h, frameStyle: frame.style.cssText });
      } else {
        console.log('[setImageBlockSize] No frame found!');
      }
      // Also update the image itself - set both inline styles AND width/height attributes for email compatibility
      if (img) {
        img.style.width = '100%';
        img.style.maxWidth = '100%';
        img.style.height = '100%';
        img.style.objectFit = 'contain';
        // Set width/height attributes so they persist in email clients that strip CSS
        img.setAttribute('width', String(w));
        img.setAttribute('height', String(h));
      }
      // Update overlay if this is the selected block
      if (block === this.selectedImageBlock) {
        this.updateSelectedImageOverlay();
      }
    } catch (e) {
      console.error('[setImageBlockSize] Error:', e);
    }
  }

  private setSelectedImage(block: HTMLElement) {
    console.log('[setSelectedImage] Called with block:', block.getAttribute('data-image-id'));
    console.log('[setSelectedImage] Block HTML:', block.outerHTML.substring(0, 200));

    // Remove border from previously selected image
    if (this.selectedImageBlock) {
      const prevImg = this.selectedImageBlock.querySelector('img.image-content') as HTMLImageElement;
      const prevBlock = this.selectedImageBlock;
      if (prevImg) {
        prevImg.style.removeProperty('border');
        prevImg.style.removeProperty('outline');
        prevImg.style.removeProperty('box-shadow');
        prevImg.style.removeProperty('box-sizing');
        prevImg.classList.remove('selected');
      }
      if (prevBlock) {
        prevBlock.classList.remove('selected');
      }
    }

    this.selectedImageBlock = block;
    this.showImageOverlay = true;
    this.isCropping = false;
    this.cropDraft = null;

    // Add border to newly selected image
    const img = block.querySelector('img.image-content') as HTMLImageElement;
    const frame = block.querySelector('.image-frame') as HTMLElement;

    console.log('[setSelectedImage] Found elements:', {
      img: img ? 'exists' : 'null',
      frame: frame ? 'exists' : 'null',
      frameStyle: frame ? frame.style.cssText : 'n/a',
      storedW: block.getAttribute('data-image-w'),
      storedH: block.getAttribute('data-image-h'),
      storedAlign: block.getAttribute('data-image-align')
    });

    // CRITICAL FIX: Restore alignment from data attribute
    const storedAlign = (block.getAttribute('data-image-align') as ImageAlign | null) || 'left';
    block.classList.remove('align-left', 'align-center', 'align-right');
    block.classList.add(`align-${storedAlign}`);
    console.log('[setSelectedImage] Restored alignment:', storedAlign);

    // CRITICAL FIX: Restore frame size from data attributes if they exist
    // This ensures the image maintains its size after being deselected and reselected
    const storedW = parseFloat(block.getAttribute('data-image-w') || '0');
    const storedH = parseFloat(block.getAttribute('data-image-h') || '0');

    if (storedW > 0 && storedH > 0) {
      console.log('[setSelectedImage] Restoring frame size from data attributes:', { storedW, storedH });

      // Force the size via setImageBlockSize to ensure all related attributes are set correctly
      this.setImageBlockSize(block, storedW, storedH);

      // Double-check: also set frame and img styles directly
      if (frame) {
        frame.style.width = `${storedW}px`;
        frame.style.height = `${storedH}px`;
        console.log('[setSelectedImage] Frame style after setting:', frame.style.cssText);
      }

      if (img) {
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.objectFit = 'contain';
        img.setAttribute('width', String(Math.round(storedW)));
        img.setAttribute('height', String(Math.round(storedH)));
      }

      // Re-apply crop rendering to ensure image is positioned correctly inside the frame
      const crop = this.readCropFromBlock(block);
      this.writeCropToBlock(block, crop);
    }

    if (img) {
      img.style.border = '3px solid #2196f3';
      img.style.outline = '1px solid rgba(255, 255, 255, 0.8)';
      img.style.boxSizing = 'border-box';
      // Don't add .selected class - we control the border via inline styles
    }

    console.log('[setSelectedImage] About to call updateSelectedImageOverlay');

    // Force update immediately
    this.updateSelectedImageOverlay();

    // Also retry after DOM has painted to ensure frame is fully rendered
    requestAnimationFrame(() => {
      console.log('[setSelectedImage] RAF update overlay');
      this.updateSelectedImageOverlay();

      // One more retry after a short delay if still zero
      if (this.imageOverlay.width === 0 || this.imageOverlay.height === 0) {
        setTimeout(() => {
          console.log('[setSelectedImage] Final delayed update');
          this.updateSelectedImageOverlay();
        }, 200);
      }
    });

    console.log('[setSelectedImage] After update - showImageOverlay:', this.showImageOverlay, 'overlay:', this.imageOverlay);
  }

  private clearSelectedImage() {
    console.log('[clearSelectedImage] Called');

    // Store reference to the image block before clearing
    const imageBlock = this.selectedImageBlock;

    // Remove border from selected image
    if (this.selectedImageBlock) {
      const img = this.selectedImageBlock.querySelector('img.image-content') as HTMLImageElement;
      const block = this.selectedImageBlock;

      if (img) {
        // Remove inline border styles completely
        img.style.removeProperty('border');
        img.style.removeProperty('outline');
        img.style.removeProperty('box-shadow');
        img.style.removeProperty('box-sizing');
      }

      // Also remove selected class from both block and img if it exists
      if (block) {
        block.classList.remove('selected');
      }
      if (img) {
        img.classList.remove('selected');
      }
    }

    this.selectedImageBlock = null;
    this.showImageOverlay = false;
    this.isCropping = false;
    this.cropDraft = null;

    // Restore focus to editor - let natural cursor placement happen
    try {
      const editorEl = this.getCurrentEditorEl() || this.editor?.nativeElement;
      if (editorEl) {
        // If there was an image block, try to place cursor after it
        if (imageBlock) {
          try {
            const range = document.createRange();
            const sel = window.getSelection();

            // Check if there's a node after the image block
            let nextNode = imageBlock.nextSibling;

            // Skip whitespace-only text nodes
            while (nextNode && nextNode.nodeType === Node.TEXT_NODE && !nextNode.textContent?.trim()) {
              nextNode = nextNode.nextSibling;
            }

            if (nextNode) {
              // Place cursor at the start of the next node
              if (nextNode.nodeType === Node.TEXT_NODE) {
                range.setStart(nextNode, 0);
              } else if (nextNode.nodeType === Node.ELEMENT_NODE) {
                range.setStart(nextNode, 0);
              } else {
                range.setStartBefore(nextNode);
              }
              range.collapse(true);
            } else {
              // No next sibling - ensure there's a place for cursor after image
              const parent = imageBlock.parentNode;
              if (parent && editorEl.contains(imageBlock)) {
                // Check if there's already a BR or text node after the image
                const afterImage = imageBlock.nextSibling;
                if (!afterImage) {
                  // Create a paragraph or BR for cursor placement
                  const p = document.createElement('p');
                  p.innerHTML = '<br>';
                  parent.insertBefore(p, imageBlock.nextSibling);
                  range.setStart(p, 0);
                } else {
                  range.setStartAfter(imageBlock);
                }
                range.collapse(true);
              } else {
                // Fallback
                range.setStartAfter(imageBlock);
                range.collapse(true);
              }
            }

            sel?.removeAllRanges();
            sel?.addRange(range);
            editorEl.focus();

            console.log('[clearSelectedImage] Cursor placed after image');
          } catch (e) {
            console.warn('[clearSelectedImage] Failed to place cursor after image:', e);
            // Fallback: just focus the editor
            editorEl.focus();
          }
        } else {
          // No image block - just ensure editor is focused
          editorEl.focus();
        }

        // Update caret position indicator
        this.updateCaretPosition();
      }
    } catch (e) {
      console.warn('[clearSelectedImage] Failed to restore editor focus:', e);
    }
  }

  private updateSelectedImageOverlay() {
    if (!this.selectedImageBlock || !this.showImageOverlay) {
      console.log('[updateSelectedImageOverlay] Early return:', {
        hasBlock: !!this.selectedImageBlock,
        showOverlay: this.showImageOverlay
      });
      return;
    }
    try {
      const frame = (this.selectedImageBlock.querySelector('.image-frame') as HTMLElement | null);
      const img = this.selectedImageBlock.querySelector('img.image-content') as HTMLImageElement | null;

      console.log('[updateSelectedImageOverlay] Elements found:', {
        frame: frame ? 'YES' : 'NO',
        img: img ? 'YES' : 'NO',
        frameStyle: frame?.style.cssText,
        frameWidth: frame?.style.width,
        frameHeight: frame?.style.height,
        imgNaturalSize: img ? `${img.naturalWidth}x${img.naturalHeight}` : 'n/a',
        imgSize: img ? `${img.width}x${img.height}` : 'n/a',
        imgOffsetSize: img ? `${img.offsetWidth}x${img.offsetHeight}` : 'n/a'
      });

      // CRITICAL FIX: Always measure the img element directly since it has actual content
      // The frame might have zero size if not yet laid out, but img will have dimensions
      const measureEl = img || frame || this.selectedImageBlock;
      const rect = measureEl.getBoundingClientRect();

      console.log('[updateSelectedImageOverlay] getBoundingClientRect:', {
        element: measureEl.tagName + (measureEl.className ? '.' + measureEl.className.split(' ')[0] : ''),
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
        right: rect.right,
        bottom: rect.bottom
      });

      // If dimensions are still 0, retry after a short delay
      if (rect.width === 0 || rect.height === 0) {
        console.log('[updateSelectedImageOverlay] ⚠️ Element has zero dimensions, retrying...', {
          frame: frame ? 'exists' : 'null',
          frameStyle: frame ? frame.style.cssText : 'n/a',
          rect: { width: rect.width, height: rect.height }
        });
        setTimeout(() => this.updateSelectedImageOverlay(), 50);
        return;
      }

      // Get reference container based on mode
      let wrapRect: DOMRect;
      let scrollLeft = 0;
      let scrollTop = 0;

      if (this.mode === 'compose') {
        // In compose mode, use editor element as reference
        const editorEl = this.editor?.nativeElement;
        if (!editorEl) {
          console.warn('[updateSelectedImageOverlay] No editor element in compose mode');
          return;
        }
        wrapRect = editorEl.getBoundingClientRect();
        scrollLeft = editorEl.scrollLeft || 0;
        scrollTop = editorEl.scrollTop || 0;
      } else {
        // In doc mode, use pageWrap
        if (!this.pageWrap?.nativeElement) {
          console.warn('[updateSelectedImageOverlay] No pageWrap element in doc mode');
          return;
        }
        wrapRect = this.pageWrap.nativeElement.getBoundingClientRect();
        scrollLeft = this.pageWrap.nativeElement.scrollLeft || 0;
        scrollTop = this.pageWrap.nativeElement.scrollTop || 0;
      }

      this.imageOverlay.left = rect.left - wrapRect.left + scrollLeft;
      this.imageOverlay.top = rect.top - wrapRect.top + scrollTop;
      this.imageOverlay.width = rect.width;
      this.imageOverlay.height = rect.height;

      // In compose mode, constrain overlay to stay within editor bounds (accounting for border width)
      if (this.mode === 'compose') {
        const borderWidth = 3; // blue border width
        const maxWidth = wrapRect.width - (this.imageOverlay.left + borderWidth * 2);
        const maxHeight = wrapRect.height - (this.imageOverlay.top + borderWidth * 2);

        if (this.imageOverlay.width > maxWidth) {
          this.imageOverlay.width = Math.max(0, maxWidth);
        }
        if (this.imageOverlay.height > maxHeight) {
          this.imageOverlay.height = Math.max(0, maxHeight);
        }
      }

      // Position toolbar at the left edge of the image, above it
      this.imageToolbar.left = this.imageOverlay.left;
      // Place toolbar above the image with some spacing
      this.imageToolbar.top = Math.max(8, this.imageOverlay.top - 40);

      console.log('[updateSelectedImageOverlay] ✅ SUCCESS - Final overlay values:', {
        mode: this.mode,
        measureEl: measureEl.tagName + (measureEl.className ? '.' + measureEl.className.split(' ')[0] : ''),
        frameRect: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
        wrapRect: { left: wrapRect.left, top: wrapRect.top },
        scroll: { left: scrollLeft, top: scrollTop },
        overlay: { left: this.imageOverlay.left, top: this.imageOverlay.top, width: this.imageOverlay.width, height: this.imageOverlay.height },
        toolbar: { left: this.imageToolbar.left, top: this.imageToolbar.top },
        showOverlay: this.showImageOverlay
      });
    } catch (e) {
      console.error('[updateSelectedImageOverlay] ❌ ERROR:', e);
    }
  }

  private ensureDropIndicator() {
    if (this.imageDropIndicatorEl) return;
    try {
      const el = document.createElement('div');
      el.className = 'image-drop-indicator';
      el.style.display = 'none';
      this.pageWrap.nativeElement.appendChild(el);
      this.imageDropIndicatorEl = el;
    } catch (e) { }
  }

  private hideDropIndicator() {
    try {
      if (!this.imageDropIndicatorEl) return;
      this.imageDropIndicatorEl.style.display = 'none';
    } catch (e) { }
  }

  private showDropIndicator(left: number, top: number, width: number) {
    try {
      this.ensureDropIndicator();
      if (!this.imageDropIndicatorEl) return;
      this.imageDropIndicatorEl.style.display = 'block';
      this.imageDropIndicatorEl.style.left = `${Math.max(0, Math.round(left))}px`;
      this.imageDropIndicatorEl.style.top = `${Math.max(0, Math.round(top))}px`;
      this.imageDropIndicatorEl.style.width = `${Math.max(20, Math.round(width))}px`;
    } catch (e) { }
  }

  private startImageMove(ev: MouseEvent, block: HTMLElement) {
    try {
      ev.preventDefault();
      ev.stopPropagation();
      this.moveDrag = {
        block,
        startX: ev.clientX,
        startY: ev.clientY,
        active: false,
        lastTarget: null
      };
      try { block.style.opacity = '0.65'; } catch (e) { }
    } catch (e) { }
  }

  private onImageMoveMouseMove(ev: MouseEvent) {
    if (!this.moveDrag) return;
    try {
      const d = this.moveDrag;
      const dx = ev.clientX - d.startX;
      const dy = ev.clientY - d.startY;
      if (!d.active) {
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
        d.active = true;
      }

      const t = document.elementFromPoint(ev.clientX, ev.clientY) as HTMLElement | null;
      if (!t) {
        d.lastTarget = null;
        this.hideDropIndicator();
        return;
      }

      const container =
        (t.closest('.doc-footer') as HTMLElement | null) ||
        (t.closest('.doc-header') as HTMLElement | null) ||
        (t.closest('.editor') as HTMLElement | null);
      if (!container) {
        d.lastTarget = null;
        this.hideDropIndicator();
        return;
      }

      // Determine the child node we will insert before.
      let beforeEl = t.closest('.image-block, p, table, hr, div') as HTMLElement | null;
      if (beforeEl && beforeEl === d.block) beforeEl = d.block.nextElementSibling as HTMLElement | null;

      // Ascend until direct child of container
      while (beforeEl && beforeEl.parentElement && beforeEl.parentElement !== container) {
        beforeEl = beforeEl.parentElement;
      }

      const before = beforeEl && beforeEl.parentElement === container ? (beforeEl as ChildNode) : null;
      d.lastTarget = { container, before };

      // Indicator position
      const wrapRect = this.pageWrap.nativeElement.getBoundingClientRect();
      const scrollLeft = this.pageWrap.nativeElement.scrollLeft || 0;
      const scrollTop = this.pageWrap.nativeElement.scrollTop || 0;

      if (beforeEl) {
        const r = beforeEl.getBoundingClientRect();
        this.showDropIndicator(
          r.left - wrapRect.left + scrollLeft,
          r.top - wrapRect.top + scrollTop,
          r.width
        );
      } else {
        const r = container.getBoundingClientRect();
        this.showDropIndicator(
          r.left - wrapRect.left + scrollLeft,
          r.bottom - wrapRect.top + scrollTop,
          r.width
        );
      }
    } catch (e) { }
  }

  private onImageMoveMouseUp() {
    if (!this.moveDrag) return;
    try {
      const d = this.moveDrag;
      try { d.block.style.opacity = ''; } catch (e) { }
      this.hideDropIndicator();

      if (d.active && d.lastTarget) {
        const { container, before } = d.lastTarget;
        // Avoid moving into itself
        if (before === d.block) {
          // no-op
        } else {
          try {
            if (d.block.parentNode) d.block.parentNode.removeChild(d.block);
          } catch (e) { }
          try {
            container.insertBefore(d.block, before);
          } catch (e) {
            try { container.appendChild(d.block); } catch (e2) { }
          }
          this.onEditorInput();
          this.updateSelectedImageOverlay();
        }
      }
    } catch (e) { }
    this.moveDrag = null;
  }

  onImageHandleDown(handle: string, ev: MouseEvent) {
    console.log('[onImageHandleDown] Resize handle clicked:', handle);
    ev.preventDefault();
    ev.stopPropagation();
    if (!this.selectedImageBlock) {
      console.log('[onImageHandleDown] No selected image block!');
      return;
    }
    const frame = this.selectedImageBlock.querySelector('.image-frame') as HTMLElement | null;
    if (!frame) {
      console.log('[onImageHandleDown] No frame element!');
      return;
    }
    const rect = frame.getBoundingClientRect();
    this.resizeDrag = {
      handle,
      startX: ev.clientX,
      startY: ev.clientY,
      startW: rect.width,
      startH: rect.height,
      aspect: rect.width / Math.max(1, rect.height)
    };
    console.log('[onImageHandleDown] resizeDrag initialized:', this.resizeDrag);
  }

  private onImageMouseMove(ev: MouseEvent) {
    if (this.resizeDrag && this.selectedImageBlock) {
      console.log('[onImageMouseMove] Processing resize drag');
      const d = this.resizeDrag;
      const dx = ev.clientX - d.startX;
      const dy = ev.clientY - d.startY;
      const h = d.handle;
      const isCorner = h === 'nw' || h === 'ne' || h === 'sw' || h === 'se';
      const keepAspect = isCorner && !ev.shiftKey;

      let newW = d.startW;
      let newH = d.startH;

      const applyFrom = (signX: number, signY: number) => {
        newW = d.startW + signX * dx;
        newH = d.startH + signY * dy;
      };

      if (h === 'e') applyFrom(1, 0);
      else if (h === 'w') applyFrom(-1, 0);
      else if (h === 's') applyFrom(0, 1);
      else if (h === 'n') applyFrom(0, -1);
      else if (h === 'se') applyFrom(1, 1);
      else if (h === 'sw') applyFrom(-1, 1);
      else if (h === 'ne') applyFrom(1, -1);
      else if (h === 'nw') applyFrom(-1, -1);

      if (keepAspect) {
        if (Math.abs(newW - d.startW) >= Math.abs(newH - d.startH)) newH = newW / d.aspect;
        else newW = newH * d.aspect;
      }

      console.log('[onImageMouseMove] Applying new size:', { newW, newH });
      this.setImageBlockSize(this.selectedImageBlock, newW, newH);
      this.updateSelectedImageOverlay();
    }

    if (this.cropDrag && this.isCropping && this.cropDraft && this.selectedImageBlock) {
      const cd = this.cropDrag;
      const frame = this.selectedImageBlock.querySelector('.image-frame') as HTMLElement | null;
      if (!frame) return;
      const rect = frame.getBoundingClientRect();
      const dx = (ev.clientX - cd.startX) / Math.max(1, rect.width);
      const dy = (ev.clientY - cd.startY) / Math.max(1, rect.height);

      let { x, y, w, h } = cd.start;
      const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
      const minSize = 0.05;

      if (cd.mode === 'move') {
        x = clamp01(x + dx);
        y = clamp01(y + dy);
      } else {
        const edge = cd.mode;
        if (edge.includes('n')) {
          const y2 = clamp01(y + dy);
          const newH = clamp01(h - (y2 - y));
          if (newH >= minSize) { h = newH; y = y2; }
        }
        if (edge.includes('s')) {
          const newH = clamp01(h + dy);
          if (newH >= minSize) h = newH;
        }
        if (edge.includes('w')) {
          const x2 = clamp01(x + dx);
          const newW = clamp01(w - (x2 - x));
          if (newW >= minSize) { w = newW; x = x2; }
        }
        if (edge.includes('e')) {
          const newW = clamp01(w + dx);
          if (newW >= minSize) w = newW;
        }
      }

      x = clamp01(Math.min(x, 1 - w));
      y = clamp01(Math.min(y, 1 - h));
      w = clamp01(Math.min(w, 1 - x));
      h = clamp01(Math.min(h, 1 - y));

      this.cropDraft = { enabled: true, x, y, w, h };
    }
  }

  private onImageMouseUp() {
    console.log('[onImageMouseUp] Ending resize drag');
    this.resizeDrag = null;
    this.cropDrag = null;
  }

  setImageAlignment(align: ImageAlign) {
    if (!this.selectedImageBlock) return;
    try {
      this.selectedImageBlock.classList.remove('align-left', 'align-center', 'align-right');
      this.selectedImageBlock.classList.add(`align-${align}`);
      this.selectedImageBlock.setAttribute('data-image-align', align);

      const frame = this.selectedImageBlock.querySelector('.image-frame') as HTMLElement | null;
      const img = this.selectedImageBlock.querySelector('img.image-content') as HTMLElement | null;

      // Add inline styles for email compatibility
      if (align === 'center') {
        this.selectedImageBlock.style.display = 'block';
        this.selectedImageBlock.style.width = '100%';
        this.selectedImageBlock.style.textAlign = 'center';
        this.selectedImageBlock.style.marginLeft = '';
        this.selectedImageBlock.style.marginRight = '';
        this.selectedImageBlock.style.cssFloat = 'none';

        // Update frame for center alignment
        if (frame) {
          frame.style.position = 'relative';
          frame.style.margin = '0 auto';
        }

        // Update img for center alignment
        if (img) {
          img.style.position = 'absolute';
          img.style.left = '50%';
          img.style.top = '0';
          img.style.transform = 'translateX(-50%)';
          img.style.transformOrigin = 'center top';
        }
      } else if (align === 'left') {
        this.selectedImageBlock.style.display = '';
        this.selectedImageBlock.style.width = '';
        this.selectedImageBlock.style.textAlign = '';
        this.selectedImageBlock.style.marginLeft = '';
        this.selectedImageBlock.style.marginRight = '';
        this.selectedImageBlock.style.cssFloat = 'left';

        // Update frame for left alignment
        if (frame) {
          frame.style.position = 'relative';
          frame.style.margin = '';
        }

        // Update img for left alignment
        if (img) {
          img.style.position = 'absolute';
          img.style.left = '0px';
          img.style.top = '0px';
          img.style.transform = '';
          img.style.transformOrigin = 'left top';
        }
      } else if (align === 'right') {
        this.selectedImageBlock.style.display = '';
        this.selectedImageBlock.style.width = '';
        this.selectedImageBlock.style.textAlign = '';
        this.selectedImageBlock.style.marginLeft = '';
        this.selectedImageBlock.style.marginRight = '';
        this.selectedImageBlock.style.cssFloat = 'right';

        // Update frame for right alignment
        if (frame) {
          frame.style.position = 'relative';
          frame.style.margin = '';
        }

        // Update img for right alignment
        if (img) {
          img.style.position = 'absolute';
          img.style.left = '0px';
          img.style.top = '0px';
          img.style.transform = '';
          img.style.transformOrigin = 'left top';
        }
      }

      this.updateSelectedImageOverlay();
      // CRITICAL: Persist the change by triggering onEditorInput
      this.onEditorInput();
    } catch (e) { }
  }

  startCrop() {
    if (!this.selectedImageBlock) return;
    this.isCropping = true;
    const existing = this.readCropFromBlock(this.selectedImageBlock);
    this.cropDraft = existing.enabled ? { ...existing } : { enabled: true, x: 0, y: 0, w: 1, h: 1 };
  }

  applyCrop() {
    if (!this.selectedImageBlock || !this.cropDraft) return;
    // Persist original (pre-crop) rendered size so reset is non-destructive.
    const frame = this.selectedImageBlock.querySelector('.image-frame') as HTMLElement | null;
    const rect = frame?.getBoundingClientRect();
    const curW = rect?.width || parseFloat(this.selectedImageBlock.getAttribute('data-image-w') || '0') || 0;
    const curH = rect?.height || parseFloat(this.selectedImageBlock.getAttribute('data-image-h') || '0') || 0;

    const baseWAttr = this.selectedImageBlock.getAttribute('data-crop-base-w');
    const baseHAttr = this.selectedImageBlock.getAttribute('data-crop-base-h');
    const baseW = baseWAttr ? (parseFloat(baseWAttr) || 0) : curW;
    const baseH = baseHAttr ? (parseFloat(baseHAttr) || 0) : curH;

    if (!baseWAttr && baseW > 0) this.selectedImageBlock.setAttribute('data-crop-base-w', String(Math.round(baseW)));
    if (!baseHAttr && baseH > 0) this.selectedImageBlock.setAttribute('data-crop-base-h', String(Math.round(baseH)));

    // Shrink the visible frame to match the crop area (no leftover empty space).
    const wFrac = Math.max(0.05, Math.min(1, this.cropDraft.w));
    const hFrac = Math.max(0.05, Math.min(1, this.cropDraft.h));
    const newW = baseW * wFrac;
    const newH = baseH * hFrac;
    if (newW > 0 && newH > 0) this.setImageBlockSize(this.selectedImageBlock, newW, newH);

    this.writeCropToBlock(this.selectedImageBlock, { ...this.cropDraft, enabled: true });
    this.isCropping = false;
    this.cropDraft = null;
    this.cropDrag = null;
    this.resizeDrag = null;
    this.updateSelectedImageOverlay();
  }

  cancelCrop() {
    this.isCropping = false;
    this.cropDraft = null;
    this.cropDrag = null;
    this.resizeDrag = null;
    this.updateSelectedImageOverlay();
  }

  resetCrop() {
    if (!this.selectedImageBlock) return;
    // Restore original (pre-crop) size if present
    try {
      const baseW = parseFloat(this.selectedImageBlock.getAttribute('data-crop-base-w') || '0') || 0;
      const baseH = parseFloat(this.selectedImageBlock.getAttribute('data-crop-base-h') || '0') || 0;
      if (baseW > 0 && baseH > 0) this.setImageBlockSize(this.selectedImageBlock, baseW, baseH);
      this.selectedImageBlock.removeAttribute('data-crop-base-w');
      this.selectedImageBlock.removeAttribute('data-crop-base-h');
    } catch (e) { }

    const c: ImageCrop = { enabled: false, x: 0, y: 0, w: 1, h: 1 };
    this.writeCropToBlock(this.selectedImageBlock, c);
    if (this.isCropping) this.cropDraft = { enabled: true, x: 0, y: 0, w: 1, h: 1 };
  }

  deleteSelectedImage() {
    if (!this.selectedImageBlock) return;
    try {
      const toRemove = this.selectedImageBlock;
      this.clearSelectedImage();
      toRemove.remove();
      this.onEditorInput();
    } catch (e) { }
  }

  /**
   * Place cursor before the selected image
   */
  placeCursorBeforeImage() {
    if (!this.selectedImageBlock) return;

    try {
      const editorEl = this.getCurrentEditorEl() || this.editor?.nativeElement;
      if (!editorEl) return;

      const range = document.createRange();
      const sel = window.getSelection();

      // Check if there's a node before the image
      let prevNode = this.selectedImageBlock.previousSibling;

      // Skip whitespace-only text nodes
      while (prevNode && prevNode.nodeType === Node.TEXT_NODE && !prevNode.textContent?.trim()) {
        prevNode = prevNode.previousSibling;
      }

      if (prevNode) {
        // Place cursor at the end of previous node
        if (prevNode.nodeType === Node.TEXT_NODE) {
          range.setStart(prevNode, prevNode.textContent?.length || 0);
        } else if (prevNode.nodeType === Node.ELEMENT_NODE) {
          range.setStart(prevNode, (prevNode as Element).childNodes.length);
        } else {
          range.setStartAfter(prevNode);
        }
        range.collapse(true);
      } else {
        // No previous node - create a paragraph before the image
        const parent = this.selectedImageBlock.parentNode;
        if (parent && editorEl.contains(this.selectedImageBlock)) {
          const p = document.createElement('p');
          p.innerHTML = '<br>';
          parent.insertBefore(p, this.selectedImageBlock);
          range.setStart(p, 0);
          range.collapse(true);
        } else {
          // Fallback
          range.setStartBefore(this.selectedImageBlock);
          range.collapse(true);
        }
      }

      sel?.removeAllRanges();
      sel?.addRange(range);
      editorEl.focus();

      // Deselect the image
      this.clearSelectedImage();

      console.log('[placeCursorBeforeImage] Cursor placed before image');
    } catch (e) {
      console.warn('[placeCursorBeforeImage] Failed:', e);
    }
  }

  /**
   * Place cursor after the selected image
   */
  placeCursorAfterImage() {
    if (!this.selectedImageBlock) return;

    try {
      const editorEl = this.getCurrentEditorEl() || this.editor?.nativeElement;
      if (!editorEl) return;

      const range = document.createRange();
      const sel = window.getSelection();

      // Check if there's a node after the image
      let nextNode = this.selectedImageBlock.nextSibling;

      // Skip whitespace-only text nodes
      while (nextNode && nextNode.nodeType === Node.TEXT_NODE && !nextNode.textContent?.trim()) {
        nextNode = nextNode.nextSibling;
      }

      if (nextNode) {
        // Place cursor at the start of next node
        if (nextNode.nodeType === Node.TEXT_NODE) {
          range.setStart(nextNode, 0);
        } else if (nextNode.nodeType === Node.ELEMENT_NODE) {
          range.setStart(nextNode, 0);
        } else {
          range.setStartBefore(nextNode);
        }
        range.collapse(true);
      } else {
        // No next node - create a paragraph after the image
        const parent = this.selectedImageBlock.parentNode;
        if (parent && editorEl.contains(this.selectedImageBlock)) {
          const p = document.createElement('p');
          p.innerHTML = '<br>';
          parent.insertBefore(p, this.selectedImageBlock.nextSibling);
          range.setStart(p, 0);
          range.collapse(true);
        } else {
          // Fallback
          range.setStartAfter(this.selectedImageBlock);
          range.collapse(true);
        }
      }

      sel?.removeAllRanges();
      sel?.addRange(range);
      editorEl.focus();

      // Deselect the image
      this.clearSelectedImage();

      console.log('[placeCursorAfterImage] Cursor placed after image');
    } catch (e) {
      console.warn('[placeCursorAfterImage] Failed:', e);
    }
  }

  canMoveImageUp(): boolean {
    if (!this.selectedImageBlock) return false;
    const prevSibling = this.selectedImageBlock.previousSibling;
    return !!prevSibling;
  }

  canMoveImageDown(): boolean {
    if (!this.selectedImageBlock) return false;
    const nextSibling = this.selectedImageBlock.nextSibling;
    return !!nextSibling;
  }

  moveImageUp() {
    if (!this.selectedImageBlock || !this.canMoveImageUp()) return;

    try {
      const editorEl = this.getCurrentEditorEl() || this.editor?.nativeElement;
      if (!editorEl) return;

      const prevSibling = this.selectedImageBlock.previousSibling;
      if (prevSibling) {
        // Move image before the previous element
        editorEl.insertBefore(this.selectedImageBlock, prevSibling);
        this.onEditorInput();

        // Update overlay position after moving
        setTimeout(() => {
          this.updateSelectedImageOverlay();
        }, 10);
      }
    } catch (e) {
      console.warn('[moveImageUp] Failed:', e);
    }
  }

  moveImageDown() {
    if (!this.selectedImageBlock || !this.canMoveImageDown()) return;

    try {
      const editorEl = this.getCurrentEditorEl() || this.editor?.nativeElement;
      if (!editorEl) return;

      const nextSibling = this.selectedImageBlock.nextSibling;
      if (nextSibling && nextSibling.nextSibling) {
        // Move image after the next element
        editorEl.insertBefore(this.selectedImageBlock, nextSibling.nextSibling);
        this.onEditorInput();

        // Update overlay position after moving
        setTimeout(() => {
          this.updateSelectedImageOverlay();
        }, 10);
      } else if (nextSibling) {
        // Next sibling is the last element, append after it
        editorEl.appendChild(this.selectedImageBlock);
        this.onEditorInput();

        // Update overlay position after moving
        setTimeout(() => {
          this.updateSelectedImageOverlay();
        }, 10);
      }
    } catch (e) {
      console.warn('[moveImageDown] Failed:', e);
    }
  }

  canMoveImageLeft(): boolean {
    if (!this.selectedImageBlock) return false;
    // Can move left if not already left-aligned
    const currentAlign = this.selectedImageBlock.getAttribute('data-image-align') || 'left';
    return currentAlign !== 'left';
  }

  canMoveImageRight(): boolean {
    if (!this.selectedImageBlock) return false;
    // Can move right if not already right-aligned
    const currentAlign = this.selectedImageBlock.getAttribute('data-image-align') || 'left';
    return currentAlign !== 'right';
  }

  moveImageLeft() {
    if (!this.selectedImageBlock || !this.canMoveImageLeft()) return;

    try {
      const currentAlign = this.selectedImageBlock.getAttribute('data-image-align') || 'left';

      // Move from right -> center -> left
      if (currentAlign === 'right') {
        this.setImageAlignment('center');
      } else if (currentAlign === 'center') {
        this.setImageAlignment('left');
      }
    } catch (e) {
      console.warn('[moveImageLeft] Failed:', e);
    }
  }

  moveImageRight() {
    if (!this.selectedImageBlock || !this.canMoveImageRight()) return;

    try {
      const currentAlign = this.selectedImageBlock.getAttribute('data-image-align') || 'left';

      // Move from left -> center -> right
      if (currentAlign === 'left') {
        this.setImageAlignment('center');
      } else if (currentAlign === 'center') {
        this.setImageAlignment('right');
      }
    } catch (e) {
      console.warn('[moveImageRight] Failed:', e);
    }
  }

  openImageInNewTab() {
    if (!this.selectedImageBlock) return;
    try {
      // Try to find image with class first, then fall back to any img
      let img = this.selectedImageBlock.querySelector('img.image-content') as HTMLImageElement;
      if (!img) {
        img = this.selectedImageBlock.querySelector('img') as HTMLImageElement;
      }

      if (!img || !img.src) return;

      // Get current dimensions
      const currentWidth = parseInt(this.selectedImageBlock.getAttribute('data-image-w') || '0') || img.width || img.naturalWidth;
      const currentHeight = parseInt(this.selectedImageBlock.getAttribute('data-image-h') || '0') || img.height || img.naturalHeight;

      // Create a new window/tab with an image editor interface
      const newWindow = window.open('', '_blank');
      if (!newWindow) {
        alert('Please allow pop-ups to open the image editor');
        return;
      }

      // Create a full HTML page with image editor
      newWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Image Editor - Resize</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: Arial, sans-serif; 
              background: #f5f5f5;
              display: flex;
              flex-direction: column;
              height: 100vh;
            }
            .toolbar {
              background: #fff;
              padding: 15px 20px;
              border-bottom: 1px solid #ddd;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .toolbar h2 {
              margin-bottom: 15px;
              color: #333;
            }
            .controls {
              display: flex;
              gap: 20px;
              align-items: center;
              flex-wrap: wrap;
            }
            .control-group {
              display: flex;
              align-items: center;
              gap: 8px;
            }
            .control-group label {
              font-weight: 500;
              color: #555;
              min-width: 60px;
            }
            .control-group input[type="number"] {
              width: 100px;
              padding: 6px 10px;
              border: 1px solid #ccc;
              border-radius: 4px;
              font-size: 14px;
            }
            .control-group input[type="checkbox"] {
              width: 18px;
              height: 18px;
              cursor: pointer;
            }
            button {
              padding: 8px 16px;
              border: none;
              border-radius: 4px;
              cursor: pointer;
              font-size: 14px;
              font-weight: 500;
              transition: all 0.2s;
            }
            .btn-primary {
              background: #1976d2;
              color: white;
            }
            .btn-primary:hover {
              background: #1565c0;
            }
            .btn-secondary {
              background: #e0e0e0;
              color: #333;
            }
            .btn-secondary:hover {
              background: #d0d0d0;
            }
            .preview-container {
              flex: 1;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 20px;
              overflow: auto;
            }
            .preview-wrapper {
              background: white;
              padding: 20px;
              border-radius: 8px;
              box-shadow: 0 4px 12px rgba(0,0,0,0.15);
              max-width: 100%;
              max-height: 100%;
            }
            #previewImage {
              max-width: 100%;
              height: auto;
              display: block;
              border: 2px solid #ddd;
            }
            .info {
              margin-top: 15px;
              padding: 10px;
              background: #f0f0f0;
              border-radius: 4px;
              font-size: 13px;
              color: #666;
            }
          </style>
        </head>
        <body>
          <div class="toolbar">
            <h2>Image Resize Editor</h2>
            <div class="controls">
              <div class="control-group">
                <label>Width:</label>
                <input type="number" id="widthInput" value="${currentWidth}" min="1" />
                <span>px</span>
              </div>
              <div class="control-group">
                <label>Height:</label>
                <input type="number" id="heightInput" value="${currentHeight}" min="1" />
                <span>px</span>
              </div>
              <div class="control-group">
                <input type="checkbox" id="aspectRatio" checked />
                <label for="aspectRatio" style="min-width: auto;">Lock aspect ratio</label>
              </div>
              <button class="btn-primary" onclick="applyResize()">Apply Resize</button>
              <button class="btn-secondary" onclick="resetSize()">Reset to Original</button>
              <button class="btn-secondary" onclick="window.close()">Close</button>
            </div>
            <div class="info">
              Original size: <strong id="originalSize"></strong> | 
              Current size: <strong id="currentSize"></strong>
            </div>
          </div>
          <div class="preview-container">
            <div class="preview-wrapper">
              <img id="previewImage" src="${img.src}" />
            </div>
          </div>
          
          <script>
            const previewImg = document.getElementById('previewImage');
            const widthInput = document.getElementById('widthInput');
            const heightInput = document.getElementById('heightInput');
            const aspectRatioCheckbox = document.getElementById('aspectRatio');
            const originalSize = document.getElementById('originalSize');
            const currentSize = document.getElementById('currentSize');
            
            let naturalWidth = 0;
            let naturalHeight = 0;
            let aspectRatio = 1;
            
            previewImg.onload = function() {
              naturalWidth = previewImg.naturalWidth;
              naturalHeight = previewImg.naturalHeight;
              aspectRatio = naturalWidth / naturalHeight;
              
              originalSize.textContent = naturalWidth + ' × ' + naturalHeight + ' px';
              updateCurrentSize();
              updatePreview();
            };
            
            function updateCurrentSize() {
              currentSize.textContent = widthInput.value + ' × ' + heightInput.value + ' px';
            }
            
            function updatePreview() {
              previewImg.style.width = widthInput.value + 'px';
              previewImg.style.height = heightInput.value + 'px';
              updateCurrentSize();
            }
            
            widthInput.addEventListener('input', function() {
              if (aspectRatioCheckbox.checked) {
                heightInput.value = Math.round(widthInput.value / aspectRatio);
              }
              updatePreview();
            });
            
            heightInput.addEventListener('input', function() {
              if (aspectRatioCheckbox.checked) {
                widthInput.value = Math.round(heightInput.value * aspectRatio);
              }
              updatePreview();
            });
            
            function applyResize() {
              const newWidth = parseInt(widthInput.value);
              const newHeight = parseInt(heightInput.value);
              
              if (newWidth > 0 && newHeight > 0) {
                // Send message back to parent window
                if (window.opener && !window.opener.closed) {
                  window.opener.postMessage({
                    type: 'imageResize',
                    width: newWidth,
                    height: newHeight
                  }, '*');
                  alert('Resize applied! You can close this window now.');
                } else {
                  alert('Parent window closed. Please copy these values:\\nWidth: ' + newWidth + 'px\\nHeight: ' + newHeight + 'px');
                }
              } else {
                alert('Please enter valid dimensions');
              }
            }
            
            function resetSize() {
              widthInput.value = naturalWidth;
              heightInput.value = naturalHeight;
              updatePreview();
            }
          </script>
        </body>
        </html>
      `);
      newWindow.document.close();

    } catch (e) {
      console.error('Failed to open image editor:', e);
      alert('Failed to open image editor');
    }
  }

  private handleImageResizeFromPopup(width: number, height: number) {
    if (!this.selectedImageBlock) return;
    try {
      // Check if this is a proper image-block or a plain img wrapper
      const hasImageBlock = this.selectedImageBlock.classList.contains('image-block');

      if (hasImageBlock) {
        // Use the proper setImageBlockSize for image-block structure
        this.setImageBlockSize(this.selectedImageBlock, width, height);
      } else {
        // For plain img tags, update the img element directly
        let img = this.selectedImageBlock.querySelector('img') as HTMLImageElement;
        if (!img && this.selectedImageBlock.tagName === 'IMG') {
          img = this.selectedImageBlock as any as HTMLImageElement;
        }
        if (img) {
          img.style.width = `${width}px`;
          img.style.height = `${height}px`;
          img.width = width;
          img.height = height;
        }
      }

      this.onEditorInput();
    } catch (e) {
      console.error('Failed to apply resize:', e);
    }
  }

  onCropHandleDown(mode: 'move' | 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw', ev: MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    if (!this.cropDraft) return;
    this.cropDrag = {
      mode,
      startX: ev.clientX,
      startY: ev.clientY,
      start: { ...this.cropDraft }
    };
  }

  private readCropFromBlock(block: HTMLElement): ImageCrop {
    const enabled = block.getAttribute('data-crop-enabled') === '1';
    const x = parseFloat(block.getAttribute('data-crop-x') || '0') || 0;
    const y = parseFloat(block.getAttribute('data-crop-y') || '0') || 0;
    const w = parseFloat(block.getAttribute('data-crop-w') || '1') || 1;
    const h = parseFloat(block.getAttribute('data-crop-h') || '1') || 1;
    return { enabled, x, y, w, h };
  }

  private writeCropToBlock(block: HTMLElement, crop: ImageCrop) {
    try {
      const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
      const x = clamp01(crop.x);
      const y = clamp01(crop.y);
      const w = clamp01(crop.w);
      const h = clamp01(crop.h);
      const enabled = (crop.enabled && (w < 1 || h < 1 || x > 0 || y > 0));
      block.setAttribute('data-crop-enabled', enabled ? '1' : '0');
      block.setAttribute('data-crop-x', String(x));
      block.setAttribute('data-crop-y', String(y));
      block.setAttribute('data-crop-w', String(w));
      block.setAttribute('data-crop-h', String(h));

      const img = block.querySelector('img.image-content') as HTMLElement | null;
      if (img) {
        const frameEl = block.querySelector('.image-frame') as HTMLElement | null;

        // CRITICAL: Always read from data attributes as the source of truth
        // The frame's inline style or getBoundingClientRect might be out of sync
        const storedW = parseFloat(block.getAttribute('data-image-w') || '0') || 0;
        const storedH = parseFloat(block.getAttribute('data-image-h') || '0') || 0;

        // Use stored values as the authoritative frame size
        let frameW = storedW;
        let frameH = storedH;

        // Only fall back to getBoundingClientRect if no stored values exist
        if (frameW === 0 || frameH === 0) {
          const frameRect = frameEl?.getBoundingClientRect();
          frameW = frameW || (frameRect?.width || 0);
          frameH = frameH || (frameRect?.height || 0);
        }

        const baseW = parseFloat(block.getAttribute('data-crop-base-w') || '0') || 0;
        const baseH = parseFloat(block.getAttribute('data-crop-base-h') || '0') || 0;

        // CRITICAL FIX: ALWAYS ensure the frame element has the correct size
        // This ensures frame inline styles are in sync with data attributes
        if (frameEl && frameW > 0 && frameH > 0) {
          frameEl.style.width = `${Math.round(frameW)}px`;
          frameEl.style.height = `${Math.round(frameH)}px`;
          console.log('[writeCropToBlock] Synced frame size:', {
            frameW: Math.round(frameW),
            frameH: Math.round(frameH),
            storedW,
            storedH,
            frameStyle: frameEl.style.cssText.substring(0, 100)
          });
        }

        // Check alignment for proper positioning
        const alignment = block.getAttribute('data-image-align') || 'left';

        // Always render inside frame
        (img as any).style.position = 'absolute';

        if (alignment === 'center') {
          // Center alignment: use left:50% and translateX(-50%)
          (img as any).style.left = '50%';
          (img as any).style.transformOrigin = 'center top';
        } else {
          // Left/Right alignment: use left:0
          (img as any).style.left = '0px';
          (img as any).style.transformOrigin = 'top left';
        }

        (img as any).style.top = '0px';

        if (!enabled) {
          // default: fill the frame
          (img as any).style.width = '100%';
          (img as any).style.height = '100%';

          if (alignment === 'center') {
            (img as any).style.transform = 'translateX(-50%)';
          } else {
            (img as any).style.transform = '';
          }

          (img as any).style.clipPath = '';
          block.removeAttribute('data-crop-base-w');
          block.removeAttribute('data-crop-base-h');
        } else {
          // Crop rendering:
          // - Frame is shrunk to (baseW*w, baseH*h) on Apply
          // - Underlying image is drawn at (baseW, baseH) scaled along with frame
          // - Translate so crop top-left aligns with frame
          const bw = baseW > 0 ? baseW : (frameW > 0 ? frameW / Math.max(0.0001, w) : 0);
          const bh = baseH > 0 ? baseH : (frameH > 0 ? frameH / Math.max(0.0001, h) : 0);
          if (bw > 0) block.setAttribute('data-crop-base-w', String(Math.round(bw)));
          if (bh > 0) block.setAttribute('data-crop-base-h', String(Math.round(bh)));

          // Use independent X/Y scaling based on the actual current frame size.
          // This prevents "blank space" when rounding/CSS causes width/height scaling mismatch.
          const denomW = (bw * Math.max(0.0001, w));
          const denomH = (bh * Math.max(0.0001, h));
          const scaleX = frameW > 0 && denomW > 0 ? (frameW / denomW) : 1;
          const scaleY = frameH > 0 && denomH > 0 ? (frameH / denomH) : 1;
          const imgW = bw * scaleX;
          const imgH = bh * scaleY;
          const tx = -x * imgW;
          const ty = -y * imgH;

          (img as any).style.width = `${imgW}px`;
          (img as any).style.height = `${imgH}px`;
          (img as any).style.transform = `translate(${tx}px, ${ty}px)`;
          (img as any).style.clipPath = '';
        }
      }
    } catch (e) { }
  }

  private normalizeAllImages(root?: HTMLElement) {
    try {
      const scope = root || (this.pageWrap?.nativeElement as HTMLElement);
      if (!scope) return;
      const blocks = Array.from(scope.querySelectorAll('.image-block')) as HTMLElement[];
      blocks.forEach((b) => {
        try {
          const align = (b.getAttribute('data-image-align') as ImageAlign | null) || 'left';
          b.classList.remove('align-left', 'align-center', 'align-right');
          b.classList.add(`align-${align}`);

          const w = parseFloat(b.getAttribute('data-image-w') || '0') || 0;
          const h = parseFloat(b.getAttribute('data-image-h') || '0') || 0;
          if (w > 0 && h > 0) this.setImageBlockSize(b, w, h);

          const crop = this.readCropFromBlock(b);
          this.writeCropToBlock(b, crop);

          // clamp width to content area
          const maxW = this.getMaxContentWidthFor(b);
          const wNow = parseFloat(b.getAttribute('data-image-w') || '0') || 0;
          const hNow = parseFloat(b.getAttribute('data-image-h') || '0') || 0;
          if (wNow > maxW && hNow > 0) {
            const newW = maxW;
            const newH = Math.round(hNow * (newW / wNow));
            this.setImageBlockSize(b, newW, newH);
          }
        } catch (e) { }
      });
    } catch (e) { }
  }


  // ========================================
  // TABLE SELECTION & RESIZE (MS Word-like)
  // ========================================

  /**
   * Select a table and show resize handles
   */
  selectTable(table: HTMLTableElement) {
    try {
      console.log('[selectTable] Selecting table:', table);
      this.selectedTable = table;
      this.showTableOverlay = true;
      console.log('[selectTable] showTableOverlay set to:', this.showTableOverlay);
      this.updateTableOverlay();

      // Force Angular change detection
      setTimeout(() => {
        this.updateTableOverlay();
      }, 50);
    } catch (e) {
      console.error('Error selecting table:', e);
    }
  }

  /**
   * Deselect table and hide overlay
   */
  deselectTable() {
    this.selectedTable = null;
    this.showTableOverlay = false;
    this.tableHoverState = 'none';
    this.currentHoverColumn = -1;
    this.currentHoverRow = -1;
  }

  /**
   * Update table overlay position and size
   */
  private updateTableOverlay() {
    if (!this.selectedTable) return;

    try {
      const rect = this.selectedTable.getBoundingClientRect();

      // In compose mode, calculate relative to the pageWrap, in doc mode relative to page element
      let referenceRect;
      if (this.mode === 'compose') {
        const pageWrapEl = this.pageWrap?.nativeElement;
        if (!pageWrapEl) return;
        referenceRect = pageWrapEl.getBoundingClientRect();
        const scrollLeft = pageWrapEl.scrollLeft || 0;
        const scrollTop = pageWrapEl.scrollTop || 0;

        this.tableOverlay = {
          left: rect.left - referenceRect.left + scrollLeft,
          top: rect.top - referenceRect.top + scrollTop,
          width: rect.width,
          height: rect.height
        };
      } else {
        referenceRect = this.pageEl?.nativeElement?.getBoundingClientRect() || { left: 0, top: 0 };

        this.tableOverlay = {
          left: rect.left - referenceRect.left,
          top: rect.top - referenceRect.top,
          width: rect.width,
          height: rect.height
        };
      }

      console.log('[updateTableOverlay] Table overlay updated:', {
        mode: this.mode,
        tableRect: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
        referenceRect: { left: referenceRect.left, top: referenceRect.top },
        overlay: this.tableOverlay,
        showTableOverlay: this.showTableOverlay
      });
    } catch (e) {
      console.error('Error updating table overlay:', e);
    }
  }

  /**
   * Handle mouse move over table to show resize cursors
   */
  onTableMouseMove(evt: MouseEvent) {
    // Handle active drags
    if (this.tableResizeDrag) {
      this.handleTableDrag(evt);
      return;
    }
    if (this.columnResizeDrag) {
      this.handleColumnDrag(evt);
      return;
    }
    if (this.rowResizeDrag) {
      this.handleRowDrag(evt);
      return;
    }

    // Check for hover states to change cursor
    if (!this.selectedTable) return;

    const target = evt.target as HTMLElement;
    const table = target.closest('table');
    if (table !== this.selectedTable) {
      this.tableHoverState = 'none';
      return;
    }

    // Get mouse position relative to table
    const tableRect = this.selectedTable.getBoundingClientRect();
    const relX = evt.clientX - tableRect.left;
    const relY = evt.clientY - tableRect.top;

    // Check for column border hover (vertical resize)
    const colBorderInfo = this.getColumnBorderAt(this.selectedTable, relX, relY);
    if (colBorderInfo) {
      this.tableHoverState = 'column';
      this.currentHoverColumn = colBorderInfo.colIndex;
      return;
    }

    // Check for row border hover (horizontal resize)
    const rowBorderInfo = this.getRowBorderAt(this.selectedTable, relY);
    if (rowBorderInfo) {
      this.tableHoverState = 'row';
      this.currentHoverRow = rowBorderInfo.rowIndex;
      return;
    }

    // No resize zone
    this.tableHoverState = 'none';
    this.currentHoverColumn = -1;
    this.currentHoverRow = -1;
  }

  /**
   * Get cursor style based on hover state
   */
  getTableCursor(): string {
    if (this.tableResizeDrag) return 'nwse-resize';
    if (this.columnResizeDrag || this.tableHoverState === 'column') return 'col-resize';
    if (this.rowResizeDrag || this.tableHoverState === 'row') return 'row-resize';
    return 'default';
  }

  /**
   * Check if mouse is near a column border
   */
  private getColumnBorderAt(table: HTMLTableElement, relX: number, relY: number): { colIndex: number; x: number } | null {
    const threshold = 5; // pixels on either side of border
    const rows = Array.from(table.rows);
    if (rows.length === 0) return null;

    // Use first row to determine column boundaries
    const firstRow = rows[0];
    const cells = Array.from(firstRow.cells);

    let accumulatedX = 0;
    for (let i = 0; i < cells.length - 1; i++) {
      const cell = cells[i];
      const cellRect = cell.getBoundingClientRect();
      const tableRect = table.getBoundingClientRect();
      const cellRight = cellRect.right - tableRect.left;

      if (Math.abs(relX - cellRight) < threshold) {
        return { colIndex: i, x: cellRight };
      }
    }

    return null;
  }

  /**
   * Check if mouse is near a row border
   */
  private getRowBorderAt(table: HTMLTableElement, relY: number): { rowIndex: number; y: number } | null {
    const threshold = 5; // pixels on either side of border
    const rows = Array.from(table.rows);

    let accumulatedY = 0;
    for (let i = 0; i < rows.length - 1; i++) {
      const row = rows[i];
      const rowRect = row.getBoundingClientRect();
      const tableRect = table.getBoundingClientRect();
      const rowBottom = rowRect.bottom - tableRect.top;

      if (Math.abs(relY - rowBottom) < threshold) {
        return { rowIndex: i, y: rowBottom };
      }
    }

    return null;
  }

  /**
   * Handle mousedown on table for column/row resize
   */
  onTableMouseDown(evt: MouseEvent) {
    if (!this.selectedTable) return;

    // Start column resize
    if (this.tableHoverState === 'column' && this.currentHoverColumn >= 0) {
      evt.preventDefault();
      evt.stopPropagation();
      this.startColumnResize(evt, this.currentHoverColumn);
      return;
    }

    // Start row resize
    if (this.tableHoverState === 'row' && this.currentHoverRow >= 0) {
      evt.preventDefault();
      evt.stopPropagation();
      this.startRowResize(evt, this.currentHoverRow);
      return;
    }
  }

  /**
   * Start whole table resize (from any handle)
   */
  onTableResizeHandleDown(evt: MouseEvent, handle: string = 'se') {
    evt.preventDefault();
    evt.stopPropagation();

    if (!this.selectedTable) return;

    this.tableResizeDrag = {
      table: this.selectedTable,
      handle: handle,
      startX: evt.clientX,
      startY: evt.clientY,
      startWidth: this.selectedTable.offsetWidth,
      startHeight: this.selectedTable.offsetHeight
    };
  }

  /**
   * Start column resize drag
   */
  private startColumnResize(evt: MouseEvent, colIndex: number) {
    if (!this.selectedTable) return;

    const rows = Array.from(this.selectedTable.rows);
    const cells: HTMLTableCellElement[] = [];
    const startWidths: number[] = [];

    // Collect all cells in this column
    rows.forEach(row => {
      if (row.cells[colIndex]) {
        const cell = row.cells[colIndex];
        cells.push(cell);
        startWidths.push(cell.offsetWidth);
      }
    });

    this.columnResizeDrag = {
      table: this.selectedTable,
      colIndex,
      startX: evt.clientX,
      startWidths,
      cells
    };
  }

  /**
   * Start row resize drag
   */
  private startRowResize(evt: MouseEvent, rowIndex: number) {
    if (!this.selectedTable) return;

    const row = this.selectedTable.rows[rowIndex];
    if (!row) return;

    this.rowResizeDrag = {
      table: this.selectedTable,
      rowIndex,
      startY: evt.clientY,
      startHeight: row.offsetHeight,
      row
    };
  }

  /**
   * Handle whole table drag (8 directions)
   */
  private handleTableDrag(evt: MouseEvent) {
    if (!this.tableResizeDrag) return;

    evt.preventDefault();

    const d = this.tableResizeDrag;
    const dx = evt.clientX - d.startX;
    const dy = evt.clientY - d.startY;
    const h = d.handle;

    let newWidth = d.startWidth;
    let newHeight = d.startHeight;

    // Calculate new dimensions based on handle direction
    if (h.includes('e')) newWidth = d.startWidth + dx;
    if (h.includes('w')) newWidth = d.startWidth - dx;
    if (h.includes('s')) newHeight = d.startHeight + dy;
    if (h.includes('n')) newHeight = d.startHeight - dy;

    // Apply minimum constraints
    newWidth = Math.max(100, newWidth);
    newHeight = Math.max(50, newHeight);

    this.tableResizeDrag.table.style.width = `${newWidth}px`;
    this.tableResizeDrag.table.style.height = `${newHeight}px`;

    this.updateTableOverlay();
  }

  /**
   * Handle column drag
   */
  private handleColumnDrag(evt: MouseEvent) {
    if (!this.columnResizeDrag) return;

    evt.preventDefault();

    const deltaX = evt.clientX - this.columnResizeDrag.startX;
    const minWidth = 30; // minimum column width

    // Apply new width to all cells in this column
    this.columnResizeDrag.cells.forEach((cell, idx) => {
      const newWidth = Math.max(minWidth, this.columnResizeDrag!.startWidths[idx] + deltaX);
      cell.style.width = `${newWidth}px`;
    });

    this.updateTableOverlay();
  }

  /**
   * Handle row drag
   */
  private handleRowDrag(evt: MouseEvent) {
    if (!this.rowResizeDrag) return;

    evt.preventDefault();

    const deltaY = evt.clientY - this.rowResizeDrag.startY;
    const minHeight = 20; // minimum row height
    const newHeight = Math.max(minHeight, this.rowResizeDrag.startHeight + deltaY);

    this.rowResizeDrag.row.style.height = `${newHeight}px`;

    this.updateTableOverlay();
  }

  /**
   * Handle mouse up - end any resize operation
   */
  onTableMouseUp(evt?: MouseEvent) {
    if (this.tableResizeDrag) {
      this.tableResizeDrag = null;
      this.onEditorInput();
    }
    if (this.columnResizeDrag) {
      this.columnResizeDrag = null;
      this.onEditorInput();
    }
    if (this.rowResizeDrag) {
      this.rowResizeDrag = null;
      this.onEditorInput();
    }
  }

  insertTable() {
    // Save current selection before dialog opens
    this.captureLastSelection();

    const ref = this.dialog.open(InsertTableDialogComponent, { width: '350px', height: '350px', data: { rows: 3, cols: 3 } });
    ref.afterClosed().subscribe((res: { rows: number, cols: number } | undefined) => {
      if (!res) return;
      const rows = Math.max(1, Math.floor(res.rows));
      const cols = Math.max(1, Math.floor(res.cols));

      // Generate unique ID and region (similar to image insertion pattern)
      const tableId = this.generateImageId();
      const region = this.mode === 'doc' ? this.getActiveRegion() : 'body';
      let html = `<table align="left" border="1" cellpadding="8" cellspacing="0" id="${tableId}" data-table-temp-id="${tableId}" data-table-region="${region}" style="width:100%;border-collapse:collapse;border:1px solid #ddd;">`;
      for (let r = 0; r < rows; r++) {
        html += '<tr>';
        for (let c = 0; c < cols; c++) {
          html += '<td style="border:1px solid #ddd;padding:8px;">&nbsp;</td>';
        }
        html += '</tr>';
      }
      html += '</table><p><br></p>';

      // Insert HTML (same pattern as insertComposeImageAndUpload)
      try {
        this.exec('insertHTML', html);
      } catch (e) {
        try {
          const editorEl = this.getCurrentEditorEl();
          if (editorEl) {
            editorEl.insertAdjacentHTML('beforeend', html);
            this.onEditorInput();
          }
        } catch (e2) {
          console.error('Failed to insert table:', e2);
        }
      }

      this.onEditorInput();

      // Find and select the newly inserted table (similar to insertComposeImageAndUpload pattern)
      setTimeout(() => {
        try {
          const insertedTable = document.getElementById(tableId) as HTMLTableElement | null;
          if (insertedTable) {
            console.log('[insertTable] Found inserted table:', tableId);
            // Remove the temporary ID and data attribute
            insertedTable.removeAttribute('id');
            insertedTable.removeAttribute('data-table-temp-id');
            // Select the table to show resize handles
            this.selectTable(insertedTable);
          } else {
            console.warn('[insertTable] Could not find inserted table:', tableId);
            // Fallback: try to find by data attribute
            const editorEl = this.getCurrentEditorEl();
            if (editorEl) {
              const tableByData = editorEl.querySelector(`table[data-table-temp-id="${tableId}"]`) as HTMLTableElement | null;
              if (tableByData) {
                console.log('[insertTable] Found table by data attribute');
                tableByData.removeAttribute('id');
                tableByData.removeAttribute('data-table-temp-id');
                this.selectTable(tableByData);
              }
            }
          }
        } catch (e) {
          console.error('[insertTable] Error selecting table:', e);
        }
      }, 100);
    });
  }

  /**
   * Open table properties dialog for the table at current selection/cursor
   */
  editTableProperties() {
    // Find the table element at current cursor position
    const table = this.getTableAtCursor();
    if (!table) {
      alert('Please click inside a table to edit its properties');
      return;
    }

    // Extract current table properties
    const currentProps = this.extractTableProperties(table);

    const ref = this.dialog.open(TablePropertiesDialogComponent, {
      width: '800px',
      maxWidth: '90vw',
      data: currentProps
    });

    ref.afterClosed().subscribe((newProps: TableProperties | undefined) => {
      if (!newProps) return;
      this.applyTableProperties(table, newProps);
      this.onEditorInput();
    });
  }

  /**
   * Get the table element at current cursor position
   */
  private getTableAtCursor(): HTMLTableElement | null {
    try {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return null;

      const range = sel.getRangeAt(0);
      let node: Node | null = range.commonAncestorContainer;

      // If text node, get parent element
      if (node && node.nodeType === Node.TEXT_NODE) {
        node = node.parentNode;
      }

      const el = node as HTMLElement | null;
      if (!el) return null;

      // Find closest table
      return el.closest('table') as HTMLTableElement | null;
    } catch (e) {
      console.error('Error getting table at cursor:', e);
      return null;
    }
  }

  /**
   * Extract current properties from a table element
   */
  private extractTableProperties(table: HTMLTableElement): TableProperties {
    const style = table.style;
    const computedStyle = window.getComputedStyle(table);

    // Extract width
    let widthValue = 100;
    let widthType: 'percent' | 'pixels' = 'percent';
    const widthStr = style.width || computedStyle.width;
    if (widthStr) {
      if (widthStr.includes('%')) {
        widthValue = parseFloat(widthStr);
        widthType = 'percent';
      } else if (widthStr.includes('px')) {
        widthValue = parseFloat(widthStr);
        widthType = 'pixels';
      }
    }

    // Extract border properties
    const borderWidth = parseFloat(style.borderWidth || computedStyle.borderWidth || '1');
    const borderColor = style.borderColor || computedStyle.borderColor || '#dddddd';
    const borderStyleValue = (style.borderStyle || computedStyle.borderStyle || 'solid') as 'solid' | 'dashed' | 'dotted' | 'double' | 'none';

    // Extract background color
    const backgroundColor = style.backgroundColor || computedStyle.backgroundColor || 'transparent';

    // Extract cell properties from first cell
    const firstCell = table.querySelector('td, th') as HTMLTableCellElement | null;
    let cellPadding = 8;
    let cellSpacing = 0;
    if (firstCell) {
      const cellStyle = window.getComputedStyle(firstCell);
      cellPadding = parseFloat(firstCell.style.padding || cellStyle.padding || '8');
    }

    // Extract alignment and margins
    let alignment: 'left' | 'center' | 'right' = 'left';
    const marginLeftStr = style.marginLeft || computedStyle.marginLeft;
    const marginRightStr = style.marginRight || computedStyle.marginRight;
    let marginLeft = 0;
    let marginRight = 0;

    if (marginLeftStr === 'auto' && marginRightStr === 'auto') {
      alignment = 'center';
    } else if (marginLeftStr === 'auto') {
      alignment = 'right';
    } else {
      marginLeft = parseFloat(marginLeftStr) || 0;
      marginRight = parseFloat(marginRightStr) || 0;
    }

    return {
      borderColor,
      borderWidth,
      borderStyle: borderStyleValue,
      backgroundColor,
      cellPadding,
      cellSpacing,
      width: widthStr || '100%',
      widthType,
      widthValue,
      alignment,
      marginLeft,
      marginRight
    };
  }

  /**
   * End table resize drag
   */
  private endTableResizeDrag() {
    if (this.tableResizeDrag) {
      this.tableResizeDrag = null;
      this.onEditorInput();
    }
  }

  private applyTableProperties(table: HTMLTableElement, props: TableProperties) {
    // Apply width
    table.style.width = props.width;
    table.setAttribute('width', props.width);

    // Apply border
    const borderVal = `${props.borderWidth}px ${props.borderStyle} ${props.borderColor}`;
    const isNoBorder = props.borderStyle === 'none' || props.borderWidth === 0;
    table.style.border = isNoBorder ? 'none' : borderVal;
    table.style.borderWidth = `${props.borderWidth}px`;
    table.style.borderColor = props.borderColor;
    table.style.borderStyle = props.borderStyle;
    table.style.borderCollapse = 'collapse';
    table.setAttribute('border', isNoBorder ? '0' : String(props.borderWidth));

    // Apply background color
    if (props.backgroundColor && props.backgroundColor !== 'transparent') {
      table.style.backgroundColor = props.backgroundColor;
      table.setAttribute('bgcolor', props.backgroundColor);
    } else {
      table.style.backgroundColor = '';
      table.removeAttribute('bgcolor');
    }

    // Apply cellpadding and cellspacing attributes
    table.setAttribute('cellpadding', String(props.cellPadding));
    table.setAttribute('cellspacing', String(props.cellSpacing));

    // Apply alignment attribute and margins
    table.setAttribute('align', props.alignment || 'left');
    table.style.marginLeft = `${props.marginLeft}px`;
    table.style.marginRight = `${props.marginRight}px`;

    // Override with alignment if set
    if (props.alignment === 'center') {
      table.style.marginLeft = 'auto';
      table.style.marginRight = 'auto';
    } else if (props.alignment === 'right') {
      table.style.marginLeft = 'auto';
      table.style.marginRight = `${props.marginRight}px`;
    } else if (props.alignment === 'left') {
      table.style.marginLeft = `${props.marginLeft}px`;
      table.style.marginRight = 'auto';
    }

    // Apply cell properties to all cells
    const cells = table.querySelectorAll('td, th') as NodeListOf<HTMLTableCellElement>;
    cells.forEach(cell => {
      cell.style.padding = `${props.cellPadding}px`;
      if (isNoBorder) {
        cell.style.border = 'none';
      } else {
        cell.style.border = borderVal;
        cell.style.borderWidth = `${props.borderWidth}px`;
        cell.style.borderColor = props.borderColor;
        cell.style.borderStyle = props.borderStyle;
      }
    });

    // Update table overlay if this table is currently selected
    if (this.selectedTable === table) {
      // Allow DOM to update with new styles before recalculating overlay
      requestAnimationFrame(() => {
        this.updateTableOverlay();
      });
    }
  }

  insertDrawing() {
    alert('Drawing tool not implemented yet.');
  }

  insertSymbol() {
    const symbols = ['©', '®', '™', '✓', '•', '°', '±'];
    const choice = prompt('Enter symbol or choose one: ' + symbols.join(' '));
    if (!choice) return;
    const editorEl = this.getCurrentEditorEl();
    if (editorEl) this.insertHtmlIntoEditor(editorEl, choice, 'afterHeader');
    else this.exec('insertHTML', choice);
  }

  insertHorizontalLine() {
    const editorEl = this.getCurrentEditorEl();
    const html = '<hr>';
    if (editorEl) this.insertHtmlIntoEditor(editorEl, html, 'afterHeader');
    else this.exec('insertHTML', html);
  }

  insertComment() {
    const text = prompt('Comment text');
    if (!text) return;
    const html = `<span class="doc-comment" title="${text}" style="background:#fff3cd;padding:2px 4px;border-radius:3px">[Comment]</span>`;
    const editorEl = this.getCurrentEditorEl();
    if (editorEl) this.insertHtmlIntoEditor(editorEl, html, 'afterHeader');
    else this.exec('insertHTML', html);
  }

  insertLink() {
    const raw = prompt('URL');
    if (!raw) return;

    const url = this.normalizeUrl(raw);
    if (!url) return;

    try {
      // Re-focus + restore the caret/selection into the editor.
      // Toolbar clicks can drop selection, which makes createLink behave like an insert.
      const editorEl = this.getCurrentEditorEl();
      if (editorEl) {
        this.focusAndRestoreSelectionFor(editorEl);
      } else {
        this.focusEditor();
      }

      // If user selected text, createLink should wrap ONLY that selection.
      // If the selection was lost (common after toolbar click), restore from our tracked range.
      const sel = window.getSelection();
      let range: Range | null = null;
      if (sel && sel.rangeCount) range = sel.getRangeAt(0);
      if (range && range.collapsed && this.lastSelectionRange && editorEl && this.lastSelectionHost === editorEl) {
        this.focusAndRestoreSelectionFor(editorEl);
      }

      const sel2 = window.getSelection();
      const selectedText = ((sel2 && sel2.toString()) || '').trim();
      const hasSelection = selectedText.length > 0;

      if (hasSelection) {
        // Wrap the selected text in the exact structure the compose flow expects:
        // <div><a href="..." title="..." target="_blank">SELECTED</a></div>
        // This also guarantees the visible text stays as the selection (not the URL).
        const safeTitle = this.escapeAttr(raw.trim());
        const html = `<div><a href="${url}" title="${safeTitle}" target="_blank">${this.escapeHtml(selectedText)}</a></div>`;

        // Replace current selection with our HTML.
        if (editorEl) this.insertHtmlIntoEditor(editorEl, html, 'afterHeader');
        else this.exec('insertHTML', html);
      } else {
        // No selection: insert a link block in the requested format.
        // Use the original entered value for title/text, but normalized URL for href.
        const display = raw.trim();
        const html = `<div><a href="${url}" title="${this.escapeAttr(display)}" target="_blank">${this.escapeHtml(display)}</a></div>`;
        if (editorEl) this.insertHtmlIntoEditor(editorEl, html, 'afterHeader');
        else this.exec('insertHTML', html);
      }
    } catch (e) {
      // Fallback: insert a plain anchor.
      const sel = window.getSelection();
      const selected = sel && sel.toString().trim().length ? sel.toString() : raw.trim();
      const html = `<div><a href="${url}" title="${this.escapeAttr(raw.trim())}" target="_blank">${this.escapeHtml(selected)}</a></div>`;
      const editorEl = this.getCurrentEditorEl();
      if (editorEl) this.insertHtmlIntoEditor(editorEl, html, 'afterHeader');
      else this.exec('insertHTML', html);
    }
  }

  private escapeAttr(value: string): string {
    return (value || '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  private escapeHtml(value: string): string {
    return (value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  private normalizeUrl(raw: string): string {
    try {
      const v = String(raw || '').trim();
      if (!v) return '';

      // Allow mailto/tel/anchors as-is
      if (/^(mailto:|tel:|#)/i.test(v)) return v;

      // If already has a scheme, keep.
      if (/^[a-z][a-z0-9+.-]*:\/\//i.test(v)) return v;

      // Handle common "www." / bare domains by prepending https.
      return `https://${v}`;
    } catch (e) {
      return '';
    }
  }

  private findNearestLinkAtSelection(): HTMLAnchorElement | null {
    try {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return null;
      const range = sel.getRangeAt(0);
      let node: Node | null = range.commonAncestorContainer;
      if (node && node.nodeType === Node.TEXT_NODE) node = node.parentNode;
      const el = (node as HTMLElement | null) || null;
      return (el?.closest?.('a') as HTMLAnchorElement | null) || null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Rewrite links in HTML into your tracking endpoint.
   * Safe to call right before sending email.
   */
  static applyLinkTracking(html: string, opts: {
    trackingBaseUrl: string;
    getLinkId: () => string;
    getClickedId?: () => string;
  }): string {
    try {
      const input = (html || '').toString();
      if (!input.trim()) return input;

      const parser = new DOMParser();
      const doc = parser.parseFromString(input, 'text/html');
      const anchors = Array.from(doc.querySelectorAll('a[href]')) as HTMLAnchorElement[];

      for (const a of anchors) {
        try {
          const hrefRaw = (a.getAttribute('href') || '').trim();
          if (!hrefRaw) continue;
          if (/^(mailto:|tel:|#)/i.test(hrefRaw)) continue;
          if (hrefRaw.includes('/api/webhook/clicked')) continue; // already tracked

          // Ensure URL is absolute-ish
          const ogURL = /^[a-z][a-z0-9+.-]*:\/\//i.test(hrefRaw) ? hrefRaw : `https://${hrefRaw}`;

          const linkId = opts.getLinkId();
          const clickedId = opts.getClickedId ? opts.getClickedId() : '';

          const u = new URL(opts.trackingBaseUrl);
          if (clickedId) u.searchParams.set('id', clickedId);
          u.searchParams.set('ogURL', ogURL);
          u.searchParams.set('linkId', linkId);

          a.setAttribute('href', u.toString());
          a.setAttribute('target', a.getAttribute('target') || '_blank');
        } catch (e) {
          // skip this anchor
        }
      }

      return doc.body.innerHTML;
    } catch (e) {
      return html;
    }
  }

  insertHeader() {
    const ref = this.dialog.open(HeaderFooterSettingsComponent, { data: { mode: 'header' }, width: '720px' });
    ref.afterClosed().subscribe((html: string | undefined) => {
      if (!html) return;
      // prepare and save wrapped header HTML for future pages (placeholders editable)
      const htmlToInsert = html.replace(/contenteditable=\"false\"/g, 'contenteditable="true"').replace(/class=\"hf-placeholder\"/g, 'class="hf-editable"');
      // If the template already contains a .doc-header wrapper, avoid double-wrapping
      const hasDocHeader = /class\\s*=\\s*[\"'].*doc-header.*[\"']/.test(htmlToInsert);
      this.lastHeaderHtml = hasDocHeader ? htmlToInsert : `<div class="doc-header">${htmlToInsert}</div>`;
      // apply header to all pages by default
      this.applyHeaderFooterToAllPages(html, 'header');
    });
  }

  insertFooter() {
    const ref = this.dialog.open(HeaderFooterSettingsComponent, { data: { mode: 'footer' }, width: '720px' });
    ref.afterClosed().subscribe((html: string | undefined) => {
      if (!html) return;
      // save for future pages
      this.lastFooterHtml = html;
      // apply footer to all pages by default
      this.applyHeaderFooterToAllPages(html, 'footer');
    });
  }

  private applyHeaderFooterToPage(html: string, type: 'header' | 'footer', targetOverride?: HTMLElement) {
    try {
      const pages = Array.from(this.pageWrap.nativeElement.querySelectorAll('.page')) as HTMLElement[];
      let target: HTMLElement;
      if (targetOverride) {
        target = targetOverride;
      } else {
        const idx = Math.max(0, Math.min(pages.length - 1, this.currentPage - 1));
        target = pages[idx] || this.pageEl.nativeElement;
      }
      // For footer: insert an undoable marker into the editor, and mirror the footer node to the page.
      if (type === 'footer') {
        const editorEl = target.querySelector('.editor') as HTMLElement | null;
        if (!editorEl) return;

        const id = this.generateId();
        // insert a hidden marker into editor (undoable by browser)
        const markerHtml = `<span class="footer-marker" data-footer-id="${id}" contenteditable="false" style="display:none"></span>`;
        this.insertHtmlIntoEditor(editorEl, markerHtml, 'end');

        // create footer node and append to page
        const tmp = document.createElement('div');
        tmp.innerHTML = html.trim();
        const node = tmp.firstElementChild as HTMLElement | null;
        if (!node) return;
        node.setAttribute('data-footer-id', id);
        try {
          node.style.position = 'absolute';
          node.style.left = (target.style.paddingLeft && target.style.paddingLeft !== '') ? target.style.paddingLeft : '24px';
          node.style.right = (target.style.paddingRight && target.style.paddingRight !== '') ? target.style.paddingRight : '24px';
          node.style.bottom = '24px';
          node.style.boxSizing = 'border-box';
        } catch (e) { }
        // remove any existing footer with same id/class
        const existing = target.querySelector('.doc-footer') as HTMLElement | null;
        if (existing) existing.remove();
        target.appendChild(node);

        // make placeholders editable in the footer
        try {
          const placeholders = Array.from(node.querySelectorAll('.hf-placeholder')) as HTMLElement[];
          placeholders.forEach(ph => { try { ph.setAttribute('contenteditable', 'true'); ph.classList.add('hf-editable'); ph.style.userSelect = 'text'; } catch (e) { } });
        } catch (e) { }

        this.onEditorInput(); this.updateCaretPosition();
        return;
      }

      // For header: insert into the page's editor using execCommand so browser undo/redo works
      const editorEl = target.querySelector('.editor') as HTMLElement | null;
      if (!editorEl) return;
      // prepare html: make preview placeholders editable when inserted
      const htmlToInsert = html.replace(/contenteditable=\"false\"/g, 'contenteditable=\"true\"').replace(/class=\"hf-placeholder\"/g, 'class=\"hf-editable\"');
      // wrap header so we can identify and replace existing headers
      const hasOuterDocHeader = /^\s*<div[^>]*class\s*=\s*["'].*doc-header.*["'][^>]*>/i.test(htmlToInsert.trim());
      const wrapped = hasOuterDocHeader ? htmlToInsert : `<div class="doc-header">${htmlToInsert}</div>`;
      // remove any existing header container in this editor to avoid duplicates
      try {
        const existingHeaders = Array.from(editorEl.querySelectorAll('.doc-header')) as HTMLElement[];
        existingHeaders.forEach(h => h.remove());
      } catch (e) { }
      // insert at start for header
      this.insertHtmlIntoEditor(editorEl, wrapped, 'start');
    } catch (e) { console.warn('applyHeaderFooterToPage failed', e); }
  }

  private applyHeaderFooterToAllPages(html: string, type: 'header' | 'footer') {
    const pages = Array.from(this.pageWrap.nativeElement.querySelectorAll('.page')) as HTMLElement[];
    if (!pages.length) return;
    // apply to every page
    for (const p of pages) {
      try {
        this.applyHeaderFooterToPage(html, type, p);
      } catch (e) { }
    }
  }

  private generateId() {
    return 'f_' + Math.random().toString(36).slice(2, 9) + '_' + Date.now();
  }

  private insertHtmlIntoEditor(
    editorEl: HTMLElement,
    html: string,
    place: 'start' | 'end' | 'afterHeader'
  ) {
    try {
      editorEl.focus();

      const sel = window.getSelection();
      let range: Range | null = null;

      if (sel && sel.rangeCount > 0) {
        range = sel.getRangeAt(0);
      }

      // If selection is outside this editor (common after selecting a non-editable image or clicking sidebar),
      // choose a safe insertion point.
      if (range && !editorEl.contains(range.startContainer)) {
        if (this.selectedImageBlock && editorEl.contains(this.selectedImageBlock)) {
          const r = document.createRange();
          r.setStartAfter(this.selectedImageBlock);
          r.collapse(true);
          range = r;
        } else {
          range = null;
        }
      }

      const headerNode =
        editorEl.querySelector(':scope > .doc-header') ||
        editorEl.querySelector('.doc-header');

      // 🟢 CASE 1: afterHeader but caret is INSIDE header → move caret after header
      if (
        place === 'afterHeader' &&
        headerNode &&
        range &&
        headerNode.contains(range.startContainer)
      ) {
        range = document.createRange();

        if (!headerNode.nextSibling) {
          const p = document.createElement('p');
          p.innerHTML = '&nbsp;';
          editorEl.appendChild(p);
        }

        range.setStartAfter(headerNode);
        range.collapse(true);
      }

      // 🟢 CASE 2: no selection → append at end
      if (!range) {
        range = document.createRange();
        range.selectNodeContents(editorEl);
        range.collapse(false);
      }

      // apply range
      sel?.removeAllRanges();
      sel?.addRange(range);

      // insert
      document.execCommand('insertHTML', false, html);

      this.onEditorInput();
      this.updateCaretPosition();
    } catch (e) {
      console.warn('insertHtmlIntoEditor failed', e);
    }
  }

  // return the editor element for the currently active page
  private getCurrentEditorEl(): HTMLElement | null {
    try {
      // Compose mode uses a single contenteditable (#editor) and doesn't rely on paging structure.
      if (this.mode === 'compose') {
        return (this.editor?.nativeElement as HTMLElement) || null;
      }
      const pages = Array.from(this.pageWrap.nativeElement.querySelectorAll('.page')) as HTMLElement[];
      const idx = Math.max(0, Math.min(pages.length - 1, this.currentPage - 1));
      const target = pages[idx] || this.pageEl.nativeElement;
      return target.querySelector('.editor') as HTMLElement | null;
    } catch (e) { return null; }
  }

  // handle mutations in the editor so that footer marker additions/removals are mirrored
  private onEditorMutations(mutations: MutationRecord[]) {
    try {
      for (const m of mutations) {
        // additions
        if (m.addedNodes && m.addedNodes.length) {
          m.addedNodes.forEach(n => {
            if (n instanceof HTMLElement) {
              const marker = n.closest ? (n.closest('.footer-marker') as HTMLElement | null) : null;
              if (marker && marker.dataset && marker.dataset.footerId) {
                const id = marker.dataset.footerId;
                // If a mirrored footer doesn't exist, create one by finding corresponding page and using stored template if possible
                // As a simple fallback, create an empty placeholder footer element so undo/redo will be tracked visually
                const pages = Array.from(this.pageWrap.nativeElement.querySelectorAll('.page')) as HTMLElement[];
                const idx = Math.max(0, Math.min(pages.length - 1, this.currentPage - 1));
                const target = pages[idx] || this.pageEl.nativeElement;
                // don't duplicate
                if (target.querySelector(`[data-footer-id="${id}"]`)) return;
                const node = document.createElement('div');
                node.className = 'doc-footer';
                node.setAttribute('data-footer-id', id);
                node.textContent = '';
                try { node.style.position = 'absolute'; node.style.left = '24px'; node.style.right = '24px'; node.style.bottom = '24px'; } catch (e) { }
                target.appendChild(node);
              }
            }
          });
        }

        // removals
        if (m.removedNodes && m.removedNodes.length) {
          m.removedNodes.forEach(n => {
            if (n instanceof HTMLElement) {
              const marker = n.closest ? (n.closest('.footer-marker') as HTMLElement | null) : null;
              if (marker && marker.dataset && marker.dataset.footerId) {
                const id = marker.dataset.footerId;
                // remove mirrored footer
                try {
                  const footer = this.pageWrap.nativeElement.querySelector(`[data-footer-id="${id}"]`) as HTMLElement | null;
                  if (footer && footer.parentNode) footer.parentNode.removeChild(footer);
                } catch (e) { }
              }
            }
          });
        }
      }
    } catch (e) { }
  }

  // create a new blank page element and append it under the page wrapper
  insertBlankPage() {
    const newPage = this.createPageElement();
    this.pageWrap.nativeElement.appendChild(newPage);
    this.pageCount = this.pageWrap.nativeElement.querySelectorAll('.page').length;
    this.focusEditorElement(newPage.querySelector('.editor') as HTMLElement);
    this.currentPage = this.pageCount;
  }

  // insert a break marker in current editor and create a following blank page
  insertBreak() {
    // insert a visible break marker where the caret is
    this.exec('insertHTML', '<div style="page-break-after:always;height:1px;visibility:hidden"></div>');
    // append a new page after inserting the break
    const newPage = this.createPageElement();
    this.pageWrap.nativeElement.appendChild(newPage);
    this.pageCount = this.pageWrap.nativeElement.querySelectorAll('.page').length;
    this.focusEditorElement(newPage.querySelector('.editor') as HTMLElement);
    this.currentPage = this.pageCount;
  }

  private createPageElement(): HTMLElement {
    // If we have an existing page element, clone it so styles/structure match exactly
    try {
      const template = this.pageEl && this.pageEl.nativeElement ? this.pageEl.nativeElement as HTMLElement : null;
      if (template) {
        const clone = template.cloneNode(true) as HTMLElement;
        // clear editor contents in the cloned page
        const editor = clone.querySelector('.editor') as HTMLElement | null;
        if (editor) {
          // seed header from last applied header (if any) so new pages inherit header
          editor.innerHTML = this.lastHeaderHtml ? this.lastHeaderHtml : '';
          editor.contentEditable = 'true';
          // remove any ids or angular-specific attributes if present
          editor.removeAttribute('ng-reflect-ng-if');
          // wire input/selection listeners so counts and caret update
          editor.addEventListener('input', () => this.onEditorInput());
          editor.addEventListener('keyup', () => this.updateCaretPosition());
          editor.addEventListener('mouseup', () => this.updateCaretPosition());
          // make any header placeholders editable when seeded
          if (this.lastHeaderHtml) {
            try {
              const placeholders = Array.from(editor.querySelectorAll('.hf-placeholder, .hf-editable')) as HTMLElement[];
              placeholders.forEach(ph => { try { ph.setAttribute('contenteditable', 'true'); ph.classList.add('hf-editable'); ph.style.userSelect = 'text'; } catch (e) { } });
            } catch (e) { }
          }
        }
        // ensure seeded footer is appended if configured and not already present
        if (this.lastFooterHtml) {
          try {
            // don't duplicate if clone already has a footer
            if (!clone.querySelector('.doc-footer')) {
              const tmp = document.createElement('div');
              tmp.innerHTML = this.lastFooterHtml.trim();
              const node = tmp.firstElementChild as HTMLElement | null;
              if (node) {
                const fid = this.generateId();
                node.setAttribute('data-footer-id', fid);
                node.classList.add('doc-footer');
                try {
                  node.style.position = 'absolute';
                  node.style.left = (clone.style.paddingLeft && clone.style.paddingLeft !== '') ? clone.style.paddingLeft : '24px';
                  node.style.right = (clone.style.paddingRight && clone.style.paddingRight !== '') ? clone.style.paddingRight : '24px';
                  node.style.bottom = '24px';
                  node.style.boxSizing = 'border-box';
                } catch (e) { }
                // make placeholders editable
                try {
                  const placeholders = Array.from(node.querySelectorAll('.hf-placeholder')) as HTMLElement[];
                  placeholders.forEach(ph => { try { ph.setAttribute('contenteditable', 'true'); ph.classList.add('hf-editable'); ph.style.userSelect = 'text'; } catch (e) { } });
                } catch (e) { }
                clone.appendChild(node);
              }
            }
          } catch (e) { }
        }
        // add delete button only for cloned/new pages (initial template remains without it)
        try {
          if (!clone.querySelector('.page-delete')) {
            const del = document.createElement('button');
            del.type = 'button';
            del.className = 'page-delete';
            del.title = 'Delete page';
            del.innerHTML = '<i class="material-icons">delete</i>';
            clone.appendChild(del);
          }
        } catch (e) { }
        return clone;
      }
    } catch (e) { }

    // fallback to creating a minimal page if template not available
    const page = document.createElement('div');
    page.className = 'page';
    const editorEl = document.createElement('div');
    editorEl.className = 'editor';
    editorEl.contentEditable = 'true';
    editorEl.style.minHeight = '800px';
    editorEl.addEventListener('input', () => this.onEditorInput());
    editorEl.addEventListener('keyup', () => this.updateCaretPosition());
    editorEl.addEventListener('mouseup', () => this.updateCaretPosition());
    page.appendChild(editorEl);
    // add delete button to newly created fallback pages
    try {
      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'page-delete';
      del.title = 'Delete page';
      del.innerHTML = '<i class="material-icons">delete</i>';
      page.appendChild(del);
    } catch (e) { }
    return page;
  }

  private focusEditorElement(el?: HTMLElement | null) {
    if (!el) return;
    // move focus to new editor and put caret at start
    el.focus();
    try {
      const range = document.createRange();
      range.setStart(el, 0);
      range.collapse(true);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    } catch (e) { }
    this.onEditorInput();
    this.updateCaretPosition();
  }

  // Remove a page element from the document and update counters
  private deletePageElement(page: HTMLElement) {
    try {
      const pages = Array.from(this.pageWrap.nativeElement.querySelectorAll('.page')) as HTMLElement[];
      const idx = pages.indexOf(page);
      if (idx === -1) return;
      // remove the page
      page.parentNode?.removeChild(page);
      // update counts
      const newPages = Array.from(this.pageWrap.nativeElement.querySelectorAll('.page')) as HTMLElement[];
      this.pageCount = newPages.length || 0;
      // adjust currentPage
      if (this.pageCount === 0) {
        // create a new blank page if none remain
        const newPage = this.createPageElement();
        this.pageWrap.nativeElement.appendChild(newPage);
        this.pageCount = this.pageWrap.nativeElement.querySelectorAll('.page').length;
        this.currentPage = this.pageCount;
      } else if (this.currentPage > this.pageCount) {
        this.currentPage = this.pageCount;
      } else if (idx + 1 <= this.currentPage) {
        // if a page before or equal to the current was removed, move caret/page index back by one
        this.currentPage = Math.max(1, this.currentPage - 1);
      }
      this.onEditorInput();
      this.updateCaretPosition();
    } catch (e) { }
  }

  private setupCanvases() {
    // size canvases for device pixel ratio
    if (!this.topCanvas?.nativeElement || !this.pageWrap?.nativeElement) return;

    const top = this.topCanvas.nativeElement;
    const wrapper = this.pageWrap.nativeElement as HTMLElement;

    const updateSize = () => {
      const rect = wrapper.getBoundingClientRect();
      // top canvas full width
      top.style.width = rect.width + 'px';
      top.style.height = '36px';
      top.width = Math.round(rect.width * this.dpr);
      top.height = Math.round(36 * this.dpr);
      top.getContext('2d')?.scale(this.dpr, this.dpr);
    };

    updateSize();
    // redraw on resize
    window.addEventListener('resize', updateSize);
    this.listeners.push(() => window.removeEventListener('resize', updateSize));
  }

  private drawTopRuler() {
    const canvas = this.topCanvas.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // clear (use CSS px units since we scaled)
    const w = canvas.width / this.dpr;
    const h = canvas.height / this.dpr;
    ctx.clearRect(0, 0, w, h);
    // ticks every 12px (approx 1/6 inch look)
    const step = 12;
    ctx.fillStyle = '#f5f5f5';
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = 'rgba(0,0,0,0.12)';
    let majorIndex = 1;
    for (let x = 0; x <= w; x += step) {
      const isMajor = x % (step * 10) === 0;
      const len = isMajor ? 12 : (x % (step * 5) === 0 ? 8 : 5);
      ctx.beginPath();
      ctx.moveTo(x + 0.5, h - 6);
      ctx.lineTo(x + 0.5, h - 6 - len);
      ctx.stroke();
      if (isMajor) {
        ctx.fillStyle = '#666';
        ctx.font = '10px Arial';
        ctx.fillText(String(majorIndex), x + 4, 10);
        majorIndex++;
      }
    }
  }


  private updateCaretPosition() {
    try {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) { this.hideCaret(); return; }
      const range = sel.getRangeAt(0).cloneRange();
      if (!range.collapsed) { this.hideCaret(); return; }
      // insert temporary marker if no rect
      let rect = range.getBoundingClientRect();
      let marker: HTMLElement | null = null;
      if (!rect || (rect.width === 0 && rect.height === 0)) {
        marker = document.createElement('span');
        marker.textContent = '\u200B';
        marker.style.opacity = '0';
        marker.style.position = 'absolute';
        range.insertNode(marker);
        rect = marker.getBoundingClientRect();
        marker.parentNode?.removeChild(marker);
      }

      if (!rect) { this.hideCaret(); return; }

      // find which page contains the selection (walk up from anchorNode)
      const anchor = sel.anchorNode as Node | null;
      let pageNode: Element | null = null;
      if (anchor && anchor.nodeType) {
        let n: Node | null = anchor;
        while (n && n.nodeType === Node.ELEMENT_NODE === false && n.parentNode) n = n.parentNode;
        // climb up to find .page
        while (n) {
          if (n instanceof Element && n.classList.contains('page')) { pageNode = n; break; }
          n = n.parentNode;
        }
      }
      const pageEl = pageNode || this.pageEl.nativeElement;
      const pageRect = (pageEl as HTMLElement).getBoundingClientRect();
      const wrapperRect = this.pageWrap.nativeElement.getBoundingClientRect();

      // compute coords relative to page element
      const x = rect.left - pageRect.left + ((pageEl as HTMLElement).scrollLeft || 0);
      const y = rect.top - pageRect.top + ((pageEl as HTMLElement).scrollTop || 0);

      const caret = this.caretIndicator.nativeElement as HTMLElement;
      caret.style.left = x + 'px';
      caret.style.top = y + 'px';
      caret.style.height = Math.max(16, rect.height) + 'px';
      caret.style.opacity = '1';
      // update page index (1-based)
      try {
        const pages = Array.from(this.pageWrap.nativeElement.querySelectorAll('.page')) as Element[];
        const idx = pages.indexOf(pageEl as Element);
        this.currentPage = idx >= 0 ? idx + 1 : 1;
      } catch (e) { }
    } catch (e) {
      this.hideCaret();
    }
  }

  private hideCaret() {
    try { this.caretIndicator.nativeElement.style.opacity = '0'; } catch (e) { }
  }

  private startDrag(type: 'left' | 'top', e: MouseEvent) {
    e.preventDefault();
    const page = this.pageEl.nativeElement as HTMLElement;
    this.dragging.type = type;
    this.dragging.startX = e.clientX;
    this.dragging.startY = e.clientY;
    // for top handle, we'll adjust first-line indent (textIndent) horizontally
    if (type === 'top') {
      const style = getComputedStyle(this.editor.nativeElement);
      const textIndent = parseFloat(style.textIndent || '0') || 0;
      this.dragging.startValue = textIndent;
      this.topHandle.nativeElement.classList.add('dragging');
      try { this.handleTooltip.nativeElement.style.display = 'block'; } catch (e) { }
    }
    (e.target as HTMLElement).style.cursor = 'grabbing';
  }

  private onDragMove(e: MouseEvent) {
    if (!this.dragging.type) return;
    const page = this.pageEl.nativeElement as HTMLElement;
    if (this.dragging.type === 'top') {
      // horizontal drag -> adjust first-line indent
      const dx = e.clientX - (this.dragging.startX || 0);
      const startIndent = this.dragging.startValue || 0;
      const newVal = Math.max(0, startIndent + dx);
      // apply indent to editor's first line (text-indent)
      this.editor.nativeElement.style.textIndent = `${newVal}px`;
      // move visual handle relative to pageWrap
      const wrapRect = this.pageWrap.nativeElement.getBoundingClientRect();
      const pageRect = this.pageEl.nativeElement.getBoundingClientRect();
      const leftPos = (pageRect.left - wrapRect.left) + newVal;
      this.topHandle.nativeElement.style.left = `${leftPos}px`;
    }
    // redraw rulers because padding changed
    this.drawTopRuler();
  }

  private endDrag() {
    this.dragging.type = null;
    try { this.topHandle.nativeElement.style.cursor = 'grab'; this.topHandle.nativeElement.classList.remove('dragging'); this.handleTooltip.nativeElement.style.display = 'none'; } catch (e) { }
  }

  private positionTopHandleFromIndent() {
    try {
      const style = getComputedStyle(this.editor.nativeElement);
      const indent = parseFloat(style.textIndent || '0') || 0;
      const wrapRect = this.pageWrap.nativeElement.getBoundingClientRect();
      const pageRect = this.pageEl.nativeElement.getBoundingClientRect();
      const leftPos = (pageRect.left - wrapRect.left) + indent;
      this.topHandle.nativeElement.style.left = `${leftPos}px`;
    } catch (e) { }
  }


  // spacing controls
  lineSpacingMode: 'single' | '1.5' | 'double' | 'multiple' = 'single';
  lineSpacingValue = 1.15;

  charSpacing = 0; // unit: pt

  spacingBefore = 0;
  spacingAfter = 0;

  indentLeft = 0;
  indentRight = 0;
  indentValue = 0;
  indentType: 'first' | 'hanging' = 'first';

  private getCurrentBlock(): HTMLElement | null {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;

    let node = sel.anchorNode as Node | null;
    while (node && node.nodeType !== 1) node = node.parentNode;
    return (node as HTMLElement)?.closest('p, div') || null;
  }

  // return all block elements affected by current selection (unique). If selection empty, returns []
  private getSelectedBlocks(): HTMLElement[] {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return [];
    const range = sel.getRangeAt(0);
    if (range.collapsed) return [];

    const editor = this.getCurrentEditorEl();
    if (!editor) return [];

    const walker = document.createTreeWalker(editor, NodeFilter.SHOW_ELEMENT, {
      acceptNode: (node: Node) => {
        const el = node as HTMLElement;
        if (!el) return NodeFilter.FILTER_REJECT;
        const tag = el.tagName.toLowerCase();
        if (tag !== 'p' && tag !== 'div') return NodeFilter.FILTER_SKIP;
        try {
          const nodeRange = document.createRange();
          nodeRange.selectNodeContents(el);
          // overlap detection: nodeStart <= selEnd && nodeEnd >= selStart
          const startCmp = nodeRange.compareBoundaryPoints(Range.START_TO_END, range);
          const endCmp = nodeRange.compareBoundaryPoints(Range.END_TO_START, range);
          if (startCmp <= 0 && endCmp >= 0) return NodeFilter.FILTER_ACCEPT;
        } catch (e) { }
        return NodeFilter.FILTER_SKIP;
      }
    } as any);

    const blocks: HTMLElement[] = [];
    let n = walker.nextNode() as HTMLElement | null;
    while (n) {
      if (!blocks.includes(n)) blocks.push(n);
      n = walker.nextNode() as HTMLElement | null;
    }
    return blocks;
  }

  private applyLineHeightToElements(elements: HTMLElement[], lh: string) {
    elements.forEach(el => { try { el.style.lineHeight = lh; } catch (e) { } });
  }

  applyLineSpacing() {
    const selBlocks = this.getSelectedBlocks();
    const editor = this.getCurrentEditorEl();
    if (!editor) return;

    let lh: string;
    if (this.lineSpacingMode === 'single') lh = 'normal';
    else if (this.lineSpacingMode === '1.5') lh = '1.5';
    else if (this.lineSpacingMode === 'double') lh = '2';
    else lh = String(this.lineSpacingValue);

    if (selBlocks.length) this.applyLineHeightToElements(selBlocks, lh);
    else {
      // apply to editor and all existing blocks
      try { editor.style.lineHeight = lh; } catch (e) { }
      const all = Array.from(editor.querySelectorAll('p, div')) as HTMLElement[];
      this.applyLineHeightToElements(all, lh);
    }
  }

  applyCharSpacing() {
    const selBlocks = this.getSelectedBlocks();
    const editor = this.getCurrentEditorEl();
    if (!editor) return;

    const value = `${this.charSpacing}pt`;
    if (selBlocks.length) selBlocks.forEach(b => { try { b.style.letterSpacing = value; } catch (e) { } });
    else {
      try { editor.style.letterSpacing = value; } catch (e) { }
      const all = Array.from(editor.querySelectorAll('p, div')) as HTMLElement[];
      all.forEach(b => { try { b.style.letterSpacing = value; } catch (e) { } });
    }
  }

  applyParagraphSpacing() {
    const selBlocks = this.getSelectedBlocks();
    const editor = this.getCurrentEditorEl();
    if (!editor) return;

    if (selBlocks.length) {
      selBlocks.forEach(b => { try { b.style.marginTop = `${this.spacingBefore}pt`; b.style.marginBottom = `${this.spacingAfter}pt`; } catch (e) { } });
    } else {
      try { editor.style.marginTop = `${this.spacingBefore}pt`; editor.style.marginBottom = `${this.spacingAfter}pt`; } catch (e) { }
      const all = Array.from(editor.querySelectorAll('p, div')) as HTMLElement[];
      all.forEach(b => { try { b.style.marginTop = `${this.spacingBefore}pt`; b.style.marginBottom = `${this.spacingAfter}pt`; } catch (e) { } });
    }
  }

  applyIndent() {
    const selBlocks = this.getSelectedBlocks();
    const editor = this.getCurrentEditorEl();
    if (!editor) return;

    const applyTo = (b: HTMLElement) => {
      try {
        b.style.paddingLeft = `${this.indentLeft}in`;
        b.style.paddingRight = `${this.indentRight}in`;
        if (this.indentType === 'first') b.style.textIndent = `${this.indentValue}in`;
        else { b.style.textIndent = `-${this.indentValue}in`; b.style.paddingLeft = `${this.indentLeft + this.indentValue}in`; }
      } catch (e) { }
    };

    if (selBlocks.length) selBlocks.forEach(b => applyTo(b));
    else {
      try { editor.style.paddingLeft = `${this.indentLeft}in`; editor.style.paddingRight = `${this.indentRight}in`; } catch (e) { }
      try { editor.style.textIndent = (this.indentType === 'first') ? `${this.indentValue}in` : `-${this.indentValue}in`; } catch (e) { }
      const all = Array.from(editor.querySelectorAll('p, div')) as HTMLElement[];
      all.forEach(b => applyTo(b));
    }
  }

  uploadInProgress: boolean = false;
  uploadProgress: number = 0;
  locations: string = "calsoftgroup.com";

  upload(): void {
    const files = this.getFiles();
    const data = new FormData();

    if (files.length > 0) {
      this.uploadInProgress = true;

      files.forEach((file: any) => {
        data.append('files', file.rawFile, file.name.replace(/\s/g, "_")
          .replace(/\(/g, '_')
          .replace(/\)/g, '_'));
      });

      this.fileUploadService.normalUploadFiles(data, 'gmail', this.locations).subscribe({
        next: (res: any) => {
          this.data1 = res;
          if (res && res.status_code === '500') {
            this.snackBar.open(res.status_message || 'An error occurred during upload', '×', { panelClass: 'error', verticalPosition: 'bottom', duration: 3000 });
            this.uploadInProgress = false;
            return;
          }
          this.uploadProgress = 100;
          this.uploader.clearQueue();

          if (this.myInputVariable) {
            this.myInputVariable.nativeElement.value = "";
          }

          const fileName = res?.productEntityMediaGallery?.[0]?.fileName ?? '';
          const mimeType = files[0].type;

          if (fileName) {
            this.handleUploadedFile(fileName, mimeType);
          }

          this.uploadInProgress = false;
        },
        error: (err) => {
          console.error('[FileUploader] Upload failed:', err);
          this.uploadInProgress = false;
          this.uploader.clearQueue();
          if (this.myInputVariable) {
            this.myInputVariable.nativeElement.value = "";
          }
        }
      });
    } else {
      alert("No Items Found");
    }
  }

  getFiles(): FileLikeObject[] {
    return this.uploader.queue.map((fileItem) => {
      return fileItem.file;
    });
  }

  uploadedMediaFileName!: string;
  mimeType!: string;
  uploadedMediaType!: string;
  uploadedMediaUrl!: string;


  handleUploadedFile(fileName: string, mime: string) {
    const cid = localStorage.getItem(Constant.enterPriseKey);
    const basePath = environment.commonImageApi + '/gmail/';
    fileName = fileName.split('/').pop() || '';
    this.uploadedMediaFileName = fileName;

    this.mimeType = mime;
    this.uploadedMediaUrl = `${basePath}${fileName}&cid=${cid}`;

    if (mime.includes('image')) this.uploadedMediaType = 'image';
    else if (mime.includes('video')) this.uploadedMediaType = 'video';
    else if (mime.includes('audio')) this.uploadedMediaType = 'audio';
    else this.uploadedMediaType = 'document';

    this.insertMediaToEditor(this.uploadedMediaUrl, this.uploadedMediaType);

    this.uploadInProgress = false;

    // Ensure uploader is fully reset for next upload
    this.uploader.clearQueue();
    if (this.myInputVariable && this.myInputVariable.nativeElement) {
      this.myInputVariable.nativeElement.value = '';
    }
  }


  insertMediaToEditor(url: string, type: string) {
    if (!this.editor) return;

    if (!url) {
      console.error('[insertMediaToEditor] URL is empty, aborting');
      return;
    }
    const safeUrl = url;

    console.log('[insertMediaToEditor] Inserting media:', { url, safeUrl, type });

    // Get the correct editor element (compose-editor or doc editor)
    const editorEl = this.getCurrentEditorEl() || this.editor?.nativeElement;
    if (!editorEl) return;

    // Focus editor and restore last selection
    try {
      editorEl.focus();

      // Restore the last known selection if available
      const sel = window.getSelection();
      if (this.lastSelectionRange && this.lastSelectionHost === editorEl) {
        try {
          sel?.removeAllRanges();
          sel?.addRange(this.lastSelectionRange);
        } catch (e) {
          // If restoring fails, place cursor at end
          const range = document.createRange();
          range.selectNodeContents(editorEl);
          range.collapse(false);
          sel?.removeAllRanges();
          sel?.addRange(range);
        }
      } else {
        // No saved selection, place at end of editor
        const range = document.createRange();
        range.selectNodeContents(editorEl);
        range.collapse(false);
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    } catch (e) {
      console.warn('Failed to restore selection', e);
    }

    let html = '';
    switch (type) {
      case 'image':
        // Use image-block for full manipulation (resize/crop/align) in both modes
        const id = this.generateImageId();
        const region = this.mode === 'doc' ? this.getActiveRegion() : 'body';
        const alignment: ImageAlign = 'left';

        // Create image block with temporary dimensions - will be auto-sized after image loads
        // Add a paragraph after the image so user can type immediately
        html =
          `<div class="image-block align-${alignment}" contenteditable="false" ` +
          `data-image-id="${id}" data-image-align="${alignment}" data-image-region="${region}" ` +
          `data-image-w="0" data-image-h="0" ` +
          `data-crop-enabled="0" data-crop-x="0" data-crop-y="0" data-crop-w="1" data-crop-h="1">` +
          `<div class="image-frame" style="max-width:100%;">` +
          `<img class="image-content" src="${safeUrl}" draggable="false" style="max-width:100%;height:auto;display:block;" />` +
          `</div>` +
          `</div>`;

        // Insert directly with execCommand since we already have the selection positioned
        document.execCommand('insertHTML', false, html);
        this.onEditorInput();

        // Move cursor to the paragraph after the image
        setTimeout(() => {
          try {
            const block = this.findImageBlockById(id);
            if (block) {
              const range = document.createRange();
              const sel = window.getSelection();

              // Find the paragraph after the image block
              let nextElement = block.nextSibling as HTMLElement | null;

              // If next sibling is a paragraph element, position cursor inside it
              if (nextElement && nextElement.nodeType === Node.ELEMENT_NODE && nextElement.tagName === 'P') {
                // Position cursor inside the paragraph before the <br>
                range.setStart(nextElement, 0);
                range.collapse(true);
              } else {
                // Fallback: position after the block
                range.setStartAfter(block);
                range.collapse(true);
              }

              sel?.removeAllRanges();
              sel?.addRange(range);

              editorEl.focus();
              this.updateCaretPosition();

              console.log('[insertMediaToEditor] Cursor positioned in paragraph after image');
            }
          } catch (e) {
            console.warn('[insertMediaToEditor] Failed to position cursor after image:', e);
          }
        }, 10);

        // Auto-size based on image aspect ratio to cover entire available width
        setTimeout(() => {
          const block = this.findImageBlockById(id);
          if (!block) {
            console.warn('[insertMediaToEditor] Could not find image block with id:', id);
            return;
          }
          const img = block.querySelector('img') as HTMLImageElement | null;
          if (!img) {
            console.warn('[insertMediaToEditor] Could not find img element in block');
            return;
          }
          const finalize = () => {
            const naturalW = img.naturalWidth || 1;
            const naturalH = img.naturalHeight || 1;
            const aspect = naturalW / naturalH;
            const maxW = this.getMaxContentWidthFor(block);
            let defaultW = Math.min(naturalW > 1 ? naturalW : 320, maxW);
            if (defaultW > 350 && aspect < 0.8) {
              defaultW = 250;
            }
            let defaultH = Math.round(defaultW / aspect);
            const maxH = 400;
            if (defaultH > maxH) {
              defaultH = maxH;
              defaultW = Math.round(defaultH * aspect);
            }
            console.log('[insertMediaToEditor] Finalizing image:', { naturalW, naturalH, aspect, maxW, defaultW, defaultH });
            this.setImageBlockSize(block, defaultW, defaultH);
            this.setSelectedImage(block);
            this.writeCropToBlock(block, this.readCropFromBlock(block));

            // Force change detection to ensure overlay UI updates
            this.zone.run(() => {
              this.showImageOverlay = true;
              console.log('[insertMediaToEditor] Forced zone run - showImageOverlay:', this.showImageOverlay);
            });
          };
          if (img.complete) {
            console.log('[insertMediaToEditor] Image already complete, finalizing immediately');
            finalize();
          } else {
            console.log('[insertMediaToEditor] Waiting for image to load...');
            img.onload = () => {
              console.log('[insertMediaToEditor] Image loaded!');
              finalize();
            };
            img.onerror = () => {
              console.error('[insertMediaToEditor] Image failed to load:', safeUrl);
            };
          }
        }, 0);
        break;
      case 'video':
        html = `<video controls style="max-width:100%;"><source src="${safeUrl}"></video><p><br></p>`;
        document.execCommand('insertHTML', false, html);
        this.onEditorInput();
        break;
      case 'audio':
        html = `<audio controls><source src="${safeUrl}"></audio><p><br></p>`;
        document.execCommand('insertHTML', false, html);
        this.onEditorInput();
        break;
      case 'pdf':
      case 'document':
      default:
        const rawFileName = (safeUrl.split('/').pop() || 'Attachment').split('&')[0];
        const displayName = decodeURIComponent(rawFileName);
        const icon = (type === 'pdf' || displayName.toLowerCase().endsWith('.pdf')) ? '📄' : '📎';
        const cleanServerUrl = (safeUrl || '').replace(/http:\/\/localhost:\d+\//gi, 'https://dev.flowzo.ai/');
        html = `<p class="attachment-file-wrapper" style="display:block;clear:both;margin:6px 0;"><a href="${cleanServerUrl}" target="_blank" rel="noopener noreferrer" class="attachment-file-pill" style="display:inline-flex;align-items:center;gap:8px;padding:10px 16px;background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;color:#1d4ed8;text-decoration:none;font-size:14px;font-weight:500;box-shadow:0 1px 3px rgba(0,0,0,0.05);">${icon} ${displayName}</a></p><p><br></p>`;
        document.execCommand('insertHTML', false, html);
        this.onEditorInput();
    }

    // Place caret after the inserted element
    try {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        range.collapse(false);
      }
    } catch (e) { }
  }

  private placeCaretAfter(node: Node) {
    const range = document.createRange();
    const sel = window.getSelection();
    if (!sel) return;

    range.setStartAfter(node);
    range.collapse(true);

    sel.removeAllRanges();
    sel.addRange(range);
  }

  toggleAiAssistant(event: MouseEvent): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.aiStep = 'input';
    this.aiCustomPrompt = '';
    this.aiGeneratedResult = '';
    this.captureSelectedText();
    this.fetchProviders();

    if (!this.dialogRef && this.aiAssistantTemplate) {
      this.dialogRef = this.dialog.open(this.aiAssistantTemplate, {
        width: '500px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        panelClass: 'ai-assistant-dialog',
        disableClose: false
      });

      this.dialogRef.afterClosed().subscribe(() => {
        this.dialogRef = null;
      });
    }
  }

  fetchProviders(): void {
    this.chatmodelsService.getProvidersDropdown().subscribe({
      next: (res: any) => {
        const list = res.datalist || res || [];
        this.providers = list.map((p: any) => ({
          ...p,
          provider: p.provider,
          viewValue: `${p.provider} (${p.models})`,
          models: p.models,
          providerId: p.providerId,
          apiUrl: p.apiUrl
        }));
        if (this.providers && this.providers.length > 0 && !this.selectedProviderId) {
          this.selectedProviderId = this.providers[0].providerId;
        }
      },
      error: (err) => {
        console.error('Failed to load AI providers:', err);
      }
    });
  }

  closeAiAssistant(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
      this.dialogRef = null;
    }
    this.aiCustomPrompt = '';
    this.aiGeneratedResult = '';
  }

  captureSelectedText(): void {
    try {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        const editorEl = this.getCurrentEditorEl() || this.editor?.nativeElement;
        if (editorEl && editorEl.contains(range.commonAncestorContainer)) {
          this.selectedText = sel.toString().trim();
          this.lastSelectionRange = range.cloneRange();
        } else {
          this.selectedText = '';
        }
      } else {
        this.selectedText = '';
      }
    } catch (e) {
      this.selectedText = '';
    }
  }

  applyAiPreset(preset: string): void {
    const textToEnhance = this.selectedText || (this.getCurrentEditorEl() || this.editor?.nativeElement)?.innerText || '';

    if (!textToEnhance.trim() && !this.aiCustomPrompt.trim()) {
      alert('Please enter some text in the editor first to enhance, or write a custom instruction in the prompt box.');
      return;
    }

    if (textToEnhance.trim()) {
      switch (preset) {
        case 'improve':
          this.aiCustomPrompt = 'Improve the writing of the following text. Make it flow better, sound more polished, and read naturally. Keep the HTML tags intact if there are any, and return only the polished content.';
          break;
        case 'grammar':
          this.aiCustomPrompt = 'Correct any spelling mistakes, typos, and grammatical errors in the following text. Do not rewrite it completely, just fix errors. Keep the HTML tags intact, and return only the corrected text.';
          break;
        case 'professional':
          this.aiCustomPrompt = 'Rewrite the following text in a professional, business-formal tone suitable for high-value business communications. Keep the HTML tags intact, and return only the rewritten text.';
          break;
        case 'friendly':
          this.aiCustomPrompt = 'Rewrite the following text in a warm, welcoming, friendly, and engaging tone. Keep the HTML tags intact, and return only the rewritten text.';
          break;
        case 'longer':
          this.aiCustomPrompt = 'Expand the following text with additional details, context, and rich content to make it more comprehensive and detailed. Keep the HTML tags intact, and return only the expanded text.';
          break;
        case 'shorter':
          this.aiCustomPrompt = 'Condense and summarize the following text to make it brief, concise, and to-the-point without losing critical details. Keep the HTML tags intact, and return only the shortened text.';
          break;
      }
    } else {
      const userPrompt = this.aiCustomPrompt.trim();
      switch (preset) {
        case 'improve':
        case 'grammar':
          break;
        case 'professional':
          this.aiCustomPrompt = `${userPrompt}\n\nWrite the response in a professional, business-formal tone suitable for high-value business communications.`;
          break;
        case 'friendly':
          this.aiCustomPrompt = `${userPrompt}\n\nWrite the response in a warm, welcoming, friendly, and engaging tone.`;
          break;
        case 'longer':
          this.aiCustomPrompt = `${userPrompt}\n\nWrite a detailed, comprehensive, and expanded response.`;
          break;
        case 'shorter':
          this.aiCustomPrompt = `${userPrompt}\n\nWrite a brief, concise, and to-the-point response.`;
          break;
      }
    }

    this.generateAiContent();
  }

  async generateAiContent(): Promise<void> {
    const textToEnhance = this.selectedText || (this.getCurrentEditorEl() || this.editor?.nativeElement)?.innerText || '';
    if (!textToEnhance.trim() && !this.aiCustomPrompt.trim()) {
      alert('Please enter or select some text to enhance, or write a custom instruction in the prompt box.');
      return;
    }

    this.aiStep = 'loading';
    try {
      const selected = this.providers.find(p => p.providerId == this.selectedProviderId);
      console.log('Sending to generateText:', selected);
      const text = await this.openRouterService.generateText(
        this.aiCustomPrompt, 
        textToEnhance, 
        selected
      );
      this.aiGeneratedResult = text;
      this.aiStep = 'review';
    } catch (e: any) {
      console.error('AI generation failed:', e);
      alert('AI generation failed: ' + (e.message || e));
      this.aiStep = 'input';
    }
  }

  insertAiDraft(): void {
    const editorEl = this.getCurrentEditorEl() || this.editor?.nativeElement;
    if (!editorEl) return;

    try {
      if (this.selectedText && this.lastSelectionRange) {
        // Replace the current selected range
        this.focusAndRestoreSelectionFor(editorEl);
        const sel = window.getSelection();
        if (sel) {
          sel.removeAllRanges();
          sel.addRange(this.lastSelectionRange);

          // Use insertHTML to preserve formatting/breaks if any
          document.execCommand('insertHTML', false, this.aiGeneratedResult);
        }
      } else {
        // No selection: replace full editor content
        editorEl.innerHTML = this.aiGeneratedResult;
      }

      // Trigger update events
      this.onEditorInput();
      this.cvaEmitValue();
      this.closeAiAssistant();
    } catch (e) {
      console.error('Failed to insert AI draft:', e);
      // Fallback
      editorEl.innerHTML = this.aiGeneratedResult;
      this.onEditorInput();
      this.cvaEmitValue();
      this.closeAiAssistant();
    }
  }

}
